'use client';

import { svgShapes } from '@/utils/svgShapes';
import { Listbox, Transition } from '@headlessui/react';
import { SparklesIcon } from '@heroicons/react/20/solid';
import {
    ArrowDownTrayIcon,
    ArrowLeftIcon,
    ArrowPathIcon,
    ArrowTopRightOnSquareIcon,
    ArrowUpTrayIcon,
    ArrowUturnLeftIcon,
    ArrowsRightLeftIcon,
    ChatBubbleLeftEllipsisIcon,
    CheckCircleIcon,
    CheckIcon,
    ChevronDownIcon,
    CodeBracketIcon,
    GlobeAltIcon,
    PencilSquareIcon,
    ShieldCheckIcon,
    StarIcon,
    SwatchIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { EditorDrawer } from './EditorDrawer.jsx';
import {
    DISPLAY_FONTS,
    GoogleFontLoader,
    TEXT_FONTS,
} from './GoogleFontLoader.jsx';
import { LoadingSpinner } from './LoadingSpinner.jsx';
import { MediaPanel } from './MediaPanel.jsx';
import { MDEditor } from './atoms/MDEditor.jsx';
import { useEditor } from './context/EditorContext/EditorContext.jsx';
import { useNotifications } from './context/NotificationContext/NotificationContext.jsx';
import { Backup } from './site-settings/Backup.jsx';
import { CookieConsent } from './site-settings/CookieConsent.jsx';
import { Redirects } from './site-settings/Redirects.jsx';
import FontAwesomeIconPicker from './ui/FontAwesomeIconPicker.jsx';
import { API } from './utils/api';
import { decodeLogoTextEntities, getLogoTextOverride } from '@/utils/logoText';
import { normalizeSvgFileForUpload } from '@/utils/normalizeSvgUpload';
import { version as editorVersion } from './version';

/* ---------------- tiny helpers ---------------- */
const HEX6 = /^#([0-9a-fA-F]{6})$/;
const HEX3 = /^#([0-9a-fA-F]{3})$/;
const HEX8 = /^#([0-9a-fA-F]{8})$/;
const SHADES = [
    '50',
    '100',
    '200',
    '300',
    '400',
    '500',
    '600',
    '700',
    '800',
    '900',
    '950',
];

const BUTTON_SIZE_PRESETS = [
    { key: 'x-small', label: 'X-Small' },
    { key: 'small', label: 'Small' },
    { key: 'regular', label: 'Regular' },
    { key: 'large', label: 'Large' },
    { key: 'x-large', label: 'X-Large' },
];
const BUTTON_SIZE_KEY_TO_INDEX = new Map(
    BUTTON_SIZE_PRESETS.map((preset, i) => [preset.key, i])
);

const BUTTON_FONT_SCALE_PRESETS = [
    { label: 'X-Small', scale: 0.9 },
    { label: 'Small', scale: 0.95 },
    { label: 'Regular', scale: 1 },
    { label: 'Large', scale: 1.05 },
    { label: 'X-Large', scale: 1.1 },
];

const BUTTON_BOLDNESS_PRESETS = [
    { label: 'Regular', weight: 400 },
    { label: 'Medium', weight: 500 },
    { label: 'SemiBold', weight: 600 },
    { label: 'Bold', weight: 700 },
    { label: 'ExtraBold', weight: 800 },
];

const BUTTON_GRADIENT_STATES = [
    { key: 'default', label: 'Default' },
    { key: 'hover', label: 'Hover' },
];
const BUTTON_ICON_POSITION_OPTIONS = [
    { key: 'right', label: 'Right' },
    { key: 'left', label: 'Left' },
];
const BUTTON_ICON_SIZE_OPTIONS = [
    { key: 'small', label: 'Small' },
    { key: 'medium', label: 'Medium' },
    { key: 'large', label: 'Large' },
    { key: 'x-large', label: 'X-Large' },
];
const LOGO_TEXT_SIZE_MIN = 16;
const LOGO_TEXT_SIZE_MAX = 48;
const LOGO_TEXT_SIZE_DEFAULT = 22;
const SHAPE_DIVIDER_SHAPE_OPTIONS = Object.keys(svgShapes).map((shape) => ({
    value: shape,
    label: shape
        .replace(/[-_]+/g, ' ')
        .replace(/\b\w/g, (match) => match.toUpperCase()),
}));
const BUTTON_HOVER_ANIMATION_OPTIONS = [
    { key: 'none', label: 'None' },
    { key: 'glow', label: 'Glow' },
    { key: 'gradual-fill', label: 'Gradual Fill' },
    { key: 'slide-up', label: 'Slide Up' },
    { key: 'slide-right', label: 'Slide Right' },
    { key: 'shimmer', label: 'Shimmer' },
    { key: 'rotating-border', label: 'Border Pulse' },
    { key: 'magnet', label: 'Magnet' },
    { key: 'underline-draw', label: 'Underline Draw' },
    { key: 'gradient-shift', label: 'Gradient Shift' },
    { key: 'pulse-ring', label: 'Pulse Ring' },
    { key: 'tilt', label: 'Tilt' },
    { key: 'icon-slide', label: 'Icon Slide' },
    { key: 'icon-nudge', label: 'Icon Nudge' },
    { key: 'icon-fill-slide', label: 'Icon Fill Slide' },
    { key: 'icon-show', label: 'Icon Highlight' },
    { key: 'icon-reveal', label: 'Icon Reveal' },
];
const INLINE_EDIT_BUTTON_CLASSNAME =
    'focus-visible:ring-primary-500/60 group-focus-visible-within:opacity-100 inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full text-neutral-500 opacity-0 transition-all hover:bg-primary-100 hover:text-primary-700 focus:outline-none focus-visible:ring-2 group-hover:opacity-100';

const normalizeShapeDividerShape = (value) => {
    const clean = String(value ?? '').trim();
    return SHAPE_DIVIDER_SHAPE_OPTIONS.some((option) => option.value === clean)
        ? clean
        : 'tilt';
};
const normalizeButtonHoverAnimation = (value) => {
    const normalized = String(value || '')
        .trim()
        .toLowerCase();
    if (normalized === 'slide') return 'slide-up';
    if (normalized === 'gradualfill') return 'gradual-fill';
    if (normalized === 'rotatinggradientborder') return 'rotating-border';
    if (normalized === 'rotatingborder') return 'rotating-border';
    if (normalized === 'underlinedraw') return 'underline-draw';
    if (normalized === 'gradientshift') return 'gradient-shift';
    if (normalized === 'pulsering') return 'pulse-ring';
    if (normalized === 'iconslide') return 'icon-slide';
    if (normalized === 'iconnudge') return 'icon-nudge';
    if (normalized === 'iconfillslide') return 'icon-fill-slide';
    if (normalized === 'iconcapsulesweep') return 'icon-show';
    if (normalized === 'iconhighlight') return 'icon-show';
    if (normalized === 'iconshow') return 'icon-show';
    if (normalized === 'iconreveal') return 'icon-reveal';
    return BUTTON_HOVER_ANIMATION_OPTIONS.some(
        (option) => option.key === normalized
    )
        ? normalized
        : 'none';
};
const arraysShallowEqual = (left = [], right = []) =>
    left.length === right.length &&
    left.every((value, index) => value === right[index]);
const TOKEN_SHADE_RE = /^(primary|secondary)-(\d{2,3})$/i;
const normalizeAngleValue = (value) => {
    const parsed =
        typeof value === 'number' ? value : Number.parseFloat(String(value));
    if (!Number.isFinite(parsed)) return 0;
    const normalized = Math.round(parsed) % 360;
    return normalized < 0 ? normalized + 360 : normalized;
};

const hexToRgb = (hex) => {
    const normalized = normalizeHex(hex, null);
    if (!normalized) return null;
    const raw = normalized.slice(1);
    return {
        r: Number.parseInt(raw.slice(0, 2), 16),
        g: Number.parseInt(raw.slice(2, 4), 16),
        b: Number.parseInt(raw.slice(4, 6), 16),
    };
};

const cssRgbToHex = (value) => {
    const match = String(value || '').match(
        /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i
    );
    if (!match) return null;
    const channels = match.slice(1, 4).map((part) => {
        const parsed = Number.parseInt(part, 10);
        return Math.max(0, Math.min(255, parsed));
    });
    return `#${channels
        .map((channel) => channel.toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase()}`;
};

const rgbDistance = (a, b) => {
    if (!a || !b) return Number.POSITIVE_INFINITY;
    const dr = a.r - b.r;
    const dg = a.g - b.g;
    const db = a.b - b.b;
    return dr * dr + dg * dg + db * db;
};

const pickSuggestedThemeColor = ({ baseColor, siteColors, existingColors }) => {
    const entries = Object.entries(siteColors || {})
        .map(([token, value]) => {
            const match = token.match(TOKEN_SHADE_RE);
            const hex = normalizeHex(value, null);
            if (!match || !hex) return null;
            return {
                token,
                variant: match[1].toLowerCase(),
                shade: match[2],
                shadeNum: Number.parseInt(match[2], 10),
                hex,
            };
        })
        .filter(Boolean);

    if (!entries.length) return null;

    const existing = new Set(
        (Array.isArray(existingColors) ? existingColors : [])
            .map((value) => normalizeHex(value, null))
            .filter(Boolean)
    );
    const baseRgb = hexToRgb(baseColor) || hexToRgb(entries[0].hex);
    const nearest = entries.reduce((best, entry) => {
        const distance = rgbDistance(baseRgb, hexToRgb(entry.hex));
        if (!best || distance < best.distance) {
            return { entry, distance };
        }
        return best;
    }, null)?.entry;

    const oppositeVariant =
        nearest?.variant === 'primary' ? 'secondary' : 'primary';

    const suggestions = [];
    if (nearest) {
        const oppositeSameShade = entries.find(
            (entry) =>
                entry.variant === oppositeVariant &&
                entry.shade === nearest.shade
        );
        if (oppositeSameShade) suggestions.push(oppositeSameShade.hex);

        const sameVariantNeighbors = entries
            .filter(
                (entry) =>
                    entry.variant === nearest.variant &&
                    entry.hex !== nearest.hex
            )
            .sort(
                (left, right) =>
                    Math.abs(left.shadeNum - nearest.shadeNum) -
                    Math.abs(right.shadeNum - nearest.shadeNum)
            );
        sameVariantNeighbors.forEach((entry) => suggestions.push(entry.hex));
    }

    entries
        .slice()
        .sort(
            (left, right) =>
                rgbDistance(baseRgb, hexToRgb(left.hex)) -
                rgbDistance(baseRgb, hexToRgb(right.hex))
        )
        .forEach((entry) => suggestions.push(entry.hex));

    for (const candidate of suggestions) {
        const normalized = normalizeHex(candidate, null);
        if (!normalized) continue;
        if (existing.has(normalized)) continue;
        return normalized;
    }

    return normalizeHex(entries[0]?.hex, null);
};

function normalizeHex(input, fallback) {
    if (!input) return fallback;
    let v = String(input).trim();
    if (!v) return fallback;
    if (v.toLowerCase() === 'transparent') return 'transparent';
    if (v.toLowerCase() === 'white') return '#FFFFFF';
    if (v.toLowerCase() === 'black') return '#000000';
    if (!v.startsWith('#')) v = `#${v}`;
    if (HEX3.test(v)) {
        v = v.replace(HEX3, (_, s) => {
            const [r, g, b] = s.split('');
            return `#${r}${r}${g}${g}${b}${b}`;
        });
    }
    return HEX6.test(v) || HEX8.test(v) ? v.toUpperCase() : fallback;
}

const coerceBoolean = (value) =>
    typeof value === 'string' ? value.toLowerCase().trim() === 'true' : !!value;

const normalizeNicejobPopupFooterFontSize = (value, fallback = 12) => {
    const parsed = Number.parseInt(String(value ?? '').trim(), 10);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.max(10, Math.min(18, parsed));
};

const normalizeCssColorToHex = (value, fallback) => {
    const raw = String(value || '')
        .trim()
        .toLowerCase();
    if (!raw) return fallback;
    const cssVarMatch = raw.match(/^var\(--([^)]+)\)$/i);
    if (cssVarMatch && typeof window !== 'undefined') {
        const resolved = getComputedStyle(document.documentElement)
            .getPropertyValue(`--${cssVarMatch[1]}`)
            .trim();
        return normalizeHex(resolved, fallback);
    }
    if (raw === 'white') return '#FFFFFF';
    if (raw === 'black') return '#000000';
    if (raw === 'transparent') return 'transparent';
    const rgbHex = cssRgbToHex(raw);
    if (rgbHex) return rgbHex;
    return normalizeHex(raw, fallback);
};

const clampOpacityPercent = (value) => {
    const parsed =
        typeof value === 'number' ? value : Number.parseFloat(String(value));
    if (!Number.isFinite(parsed)) return 100;
    return Math.max(0, Math.min(100, Math.round(parsed)));
};

const clampOpacityPercentFromSlider = (value) => {
    const parsed =
        typeof value === 'number' ? value : Number.parseFloat(String(value));
    if (!Number.isFinite(parsed)) return 100;
    return Math.max(1, Math.min(100, Math.round(parsed)));
};

const alphaHexToPercent = (alphaHex) => {
    const parsed = Number.parseInt(String(alphaHex || ''), 16);
    if (!Number.isFinite(parsed)) return 100;
    return clampOpacityPercent((parsed / 255) * 100);
};

const percentToAlphaHex = (percent) => {
    const clamped = clampOpacityPercent(percent);
    const alpha = Math.round((clamped / 100) * 255);
    return alpha.toString(16).padStart(2, '0').toUpperCase();
};

const splitHexAndOpacity = (value, fallbackHex = '#000000') => {
    const normalized = normalizeCssColorToHex(value, null);
    const fallback = normalizeHex(fallbackHex, '#000000');
    if (!normalized) return { color: fallback, opacity: 100 };
    if (normalized === 'transparent') return { color: fallback, opacity: 0 };

    const full = normalizeHex(normalized, fallback);
    if (!full || full === 'transparent') return { color: fallback, opacity: 0 };
    if (full.length === 9) {
        return {
            color: full.slice(0, 7),
            opacity: alphaHexToPercent(full.slice(7)),
        };
    }
    return { color: full, opacity: 100 };
};

const applyOpacityToColor = (color, opacity) => {
    const full = normalizeHex(color, null);
    if (!full || full === 'transparent') return 'transparent';
    const base = full.length === 9 ? full.slice(0, 7) : full;
    const clampedOpacity = clampOpacityPercent(opacity);
    if (clampedOpacity <= 0) return 'transparent';
    if (clampedOpacity >= 100) return base;
    return `${base}${percentToAlphaHex(clampedOpacity)}`;
};

const parseLinearGradient = (value) => {
    const raw = String(value || '').trim();
    const match = raw.match(/^linear-gradient\((.*)\)$/i);
    if (!match) return null;

    const parts = match[1]
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean);
    if (parts.length < 2) return null;

    let angle = 45;
    let startIndex = 0;
    const angleMatch = parts[0].match(/^(-?\d+(?:\.\d+)?)deg$/i);
    if (angleMatch) {
        const parsed = Number.parseFloat(angleMatch[1]);
        if (Number.isFinite(parsed)) angle = parsed;
        startIndex = 1;
    }

    const colors = parts
        .slice(startIndex)
        .map((p) => p.split(/\s+/)[0])
        .map((p) => normalizeCssColorToHex(p, null))
        .filter(Boolean);

    if (!colors.length) return null;
    return {
        angle,
        colors: colors.length === 1 ? [colors[0], colors[0]] : colors,
    };
};

const inferPaletteShadeFromButtonSource = (variant, source, siteColors) => {
    const raw = String(source || '').trim();
    if (!raw) return null;

    const tokenMatch = raw.match(/var\(--(primary|secondary)-(\d{2,3})\)/i);
    if (tokenMatch && tokenMatch[1].toLowerCase() === variant) {
        return tokenMatch[2];
    }

    const parsed = parseLinearGradient(raw);
    const sourceHex = parsed?.colors?.[0] || normalizeCssColorToHex(raw, null);
    const normalizedSourceHex = normalizeHex(sourceHex, null);
    if (!normalizedSourceHex) return null;

    return (
        SHADES.find((shade) => {
            const paletteHex = normalizeHex(
                siteColors?.[`${variant}-${shade}`],
                null
            );
            return paletteHex === normalizedSourceHex;
        }) || null
    );
};

const inferPaletteShadeFromButtonState = ({
    variant,
    state,
    siteColors,
    buttonColors,
}) => {
    const sources = [
        buttonColors?.[`${variant}-${state}-solid`],
        buttonColors?.[`${variant}-${state}`],
    ];

    if (typeof window !== 'undefined') {
        const rootStyles = getComputedStyle(document.documentElement);
        sources.push(
            document.documentElement.style.getPropertyValue(
                `--button-${variant}-${state}-solid`
            ),
            rootStyles.getPropertyValue(`--button-${variant}-${state}-solid`),
            document.documentElement.style.getPropertyValue(
                `--button-${variant}-${state}`
            ),
            rootStyles.getPropertyValue(`--button-${variant}-${state}`)
        );
    }

    for (const source of sources) {
        const shade = inferPaletteShadeFromButtonSource(
            variant,
            source,
            siteColors
        );
        if (shade) return shade;
    }

    return null;
};

const parseCssLengthToPx = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return Number.NaN;
    const parsed = Number.parseFloat(raw);
    if (!Number.isFinite(parsed)) return Number.NaN;
    if (raw.endsWith('rem') && typeof window !== 'undefined') {
        const rootFontSize = Number.parseFloat(
            getComputedStyle(document.documentElement).fontSize
        );
        return parsed * (Number.isFinite(rootFontSize) ? rootFontSize : 16);
    }
    return parsed;
};

const readVariantFromCSS = (variant) => {
    if (typeof window === 'undefined') return {};
    const doc = document.documentElement;
    const out = {};
    SHADES.forEach((s) => {
        const v =
            doc.style.getPropertyValue(`--${variant}-${s}`) ||
            getComputedStyle(doc).getPropertyValue(`--${variant}-${s}`);
        const hex = normalizeHex(v, null);
        if (hex) out[s] = hex;
    });
    return out;
};

const buildPaletteMap = () => {
    if (typeof window === 'undefined') return {};
    const root = document.documentElement;
    const map = {};
    ['primary', 'secondary'].forEach((v) => {
        SHADES.forEach((s) => {
            const val =
                root.style.getPropertyValue(`--${v}-${s}`) ||
                getComputedStyle(root).getPropertyValue(`--${v}-${s}`);
            const hex = normalizeHex(val, null);
            if (hex) map[`${v}-${s}`] = hex;
        });
    });
    return map;
};

const SITE_ICON_PACKS = [
    { key: 'classic', label: 'Classic' },
    { key: 'duotone', label: 'Duotone' },
    { key: 'sharp', label: 'Sharp' },
    { key: 'sharp-duotone', label: 'Sharp Duotone' },
];

const SITE_PACK_STYLE_OPTIONS = SITE_ICON_PACKS.reduce((acc, pack) => {
    if (pack.key === 'brands') {
        acc[pack.key] = [{ key: 'brands', label: 'Brands' }];
    } else {
        acc[pack.key] = [
            { key: 'solid', label: 'Solid' },
            { key: 'regular', label: 'Regular' },
            { key: 'light', label: 'Light' },
            { key: 'thin', label: 'Thin' },
        ];
    }
    return acc;
}, {});

const normalizeSiteIconStyle = (style, pack) => {
    const options = SITE_PACK_STYLE_OPTIONS[pack] || [];
    if (options.some((opt) => opt.key === style)) return style;
    return options[0]?.key || 'solid';
};

const JSON_LD_SCRIPT_RE =
    /<Script([^>]*)type=["']application\/ld\+json["']([^>]*)>\s*{([\s\S]*?)}\s*<\/Script>/gi;

function normalizeJsonLdScript(code = '') {
    if (!code) return '';
    return String(code).replace(
        JSON_LD_SCRIPT_RE,
        (full, beforeAttrs = '', afterAttrs = '', jsonBody = '') => {
            const trimmed = String(jsonBody).trim();
            if (
                !trimmed ||
                trimmed.startsWith('`') ||
                trimmed.startsWith('JSON.stringify')
            ) {
                return full;
            }

            const hasObjectBraces =
                trimmed.startsWith('{') && trimmed.endsWith('}');
            const normalizedJsonText = hasObjectBraces
                ? trimmed
                : `{\n${trimmed}\n}`;

            return `<Script${beforeAttrs}type="application/ld+json"${afterAttrs}>{\`
${normalizedJsonText}
\`}</Script>`;
        }
    );
}

const applyIconOverrideDom = (pack, style) => {
    if (typeof document === 'undefined' || !pack) return;
    const packTokens = {
        classic: [],
        duotone: ['fa-duotone'],
        sharp: ['fa-sharp'],
        'sharp-duotone': ['fa-sharp-duotone', 'fa-sharp', 'fa-duotone'],
        brands: ['fa-brands'],
    };
    const svgPrefixMap = {
        classic: {
            solid: 'fas',
            regular: 'far',
            light: 'fal',
            thin: 'fat',
            duotone: 'fad',
        },
        duotone: {
            solid: 'fad',
            regular: 'fad',
            light: 'fad',
            thin: 'fad',
            duotone: 'fad',
        },
        sharp: {
            solid: 'fass',
            regular: 'fasr',
            light: 'fasl',
            thin: 'fast',
            duotone: 'fasd',
        },
        'sharp-duotone': {
            solid: 'fasd',
            regular: 'fasd',
            light: 'fasd',
            thin: 'fasd',
            duotone: 'fasd',
        },
        brands: { brands: 'fab' },
    };
    const styleTokens = new Set([
        'fa-solid',
        'fa-regular',
        'fa-light',
        'fa-thin',
        'fa-brands',
    ]);
    const packTokenSet = new Set([
        'fa-sharp',
        'fa-duotone',
        'fa-sharp-duotone',
        'fa-classic',
        'fa-brands',
    ]);
    const desiredStyle =
        pack === 'brands' ? null : `fa-${normalizeSiteIconStyle(style, pack)}`;
    const desiredPack = packTokens[pack] || [];

    document.documentElement.dataset.faPackOverride = pack;
    document.documentElement.dataset.faStyleOverride =
        desiredStyle?.replace('fa-', '') || '';

    const candidates = document.querySelectorAll('[class*="fa-"]');
    candidates.forEach((el) => {
        const cls = new Set(
            (el.getAttribute('class') || '').split(/\s+/).filter(Boolean)
        );
        const hasFaClass = Array.from(cls).some((c) => c.startsWith('fa-'));
        if (!hasFaClass) return;
        if (cls.has('fa-brands')) return;
        styleTokens.forEach((token) => cls.delete(token));
        packTokenSet.forEach((token) => cls.delete(token));
        desiredPack.forEach((token) => cls.add(token));
        if (desiredStyle) cls.add(desiredStyle);
        el.setAttribute('class', Array.from(cls).join(' '));
    });

    const svgCandidates = document.querySelectorAll('svg[data-icon]');
    svgCandidates.forEach((el) => {
        const currentPrefix = el.getAttribute('data-prefix');
        if (currentPrefix === 'fab') return;
        const normalizedStyle = normalizeSiteIconStyle(style, pack);
        const targetPrefix =
            svgPrefixMap[pack]?.[normalizedStyle] || svgPrefixMap.classic.solid;
        el.setAttribute('data-prefix', targetPrefix);
        // also adjust class list to include corresponding fa-* style tokens
        const cls = new Set(
            (el.getAttribute('class') || '').split(/\s+/).filter(Boolean)
        );
        styleTokens.forEach((token) => cls.delete(token));
        packTokenSet.forEach((token) => cls.delete(token));
        desiredPack.forEach((token) => cls.add(token));
        if (desiredStyle) cls.add(desiredStyle);
        el.setAttribute('class', Array.from(cls).join(' '));
    });
};

const ICON_STYLE_TOKENS = new Set([
    'fa-solid',
    'fa-regular',
    'fa-light',
    'fa-thin',
    'fa-brands',
]);
const ICON_PACK_TOKENS = new Set([
    'fa-sharp',
    'fa-duotone',
    'fa-sharp-duotone',
    'fa-classic',
    'fa-brands',
]);

const REPLACE_PACK_TOKENS = {
    classic: [],
    duotone: ['fa-duotone'],
    sharp: ['fa-sharp'],
    'sharp-duotone': ['fa-sharp-duotone', 'fa-sharp', 'fa-duotone'],
    brands: ['fa-brands'],
};

const rewriteIconClassString = (value, pack, style) => {
    if (!value || typeof value !== 'string') return value;
    if (!value.includes('fa-')) return value;
    const tokens = value
        .split(/\s+/)
        .map((t) => t.trim())
        .filter(Boolean);
    if (tokens.includes('fa-brands')) return value;
    const cleaned = [];
    tokens.forEach((t) => {
        if (ICON_STYLE_TOKENS.has(t)) return;
        if (ICON_PACK_TOKENS.has(t)) return;
        cleaned.push(t);
    });
    (REPLACE_PACK_TOKENS[pack] || []).forEach((t) => cleaned.push(t));
    if (pack !== 'brands') {
        cleaned.push(`fa-${normalizeSiteIconStyle(style, pack)}`);
    }
    const seen = new Set();
    return cleaned
        .filter((t) => {
            if (seen.has(t)) return false;
            seen.add(t);
            return true;
        })
        .join(' ');
};

const deepRewriteIcons = (value, pack, style) => {
    let changed = false;
    const walk = (val) => {
        if (typeof val === 'string') {
            const next = rewriteIconClassString(val, pack, style);
            if (next !== val) changed = true;
            return next;
        }
        if (Array.isArray(val)) {
            const arr = val.map((v) => walk(v));
            if (arr.some((v, i) => v !== val[i])) changed = true;
            return arr;
        }
        if (val && typeof val === 'object') {
            const out = Array.isArray(val) ? [] : {};
            let localChanged = false;
            Object.entries(val).forEach(([k, v]) => {
                const next = walk(v);
                if (next !== v) localChanged = true;
                out[k] = next;
            });
            if (localChanged) changed = true;
            return out;
        }
        return val;
    };
    const result = walk(value);
    return { value: result, changed };
};

/* ---------------- primary/secondary color fields ---------------- */
function ThemeColorField({
    label,
    appliedHex,
    defaultColor,
    onApply,
    busy = false,
    below,
}) {
    const [local, setLocal] = useState(appliedHex || defaultColor);

    useEffect(
        () => setLocal(appliedHex || defaultColor),
        [appliedHex, defaultColor]
    );

    const current = useMemo(
        () => normalizeHex(local, appliedHex || defaultColor),
        [local, appliedHex, defaultColor]
    );

    const commitIfChanged = () => {
        const next = normalizeHex(local, appliedHex || defaultColor);
        if (next !== (appliedHex || defaultColor)) onApply(next);
    };

    return (
        <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold leading-5 text-neutral-400">
                {label}
            </label>

            <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                <div className="flex h-[38px] items-stretch overflow-hidden rounded-[10px] bg-white">
                    {/* color patch + hidden input */}
                    <div className="relative h-full w-9 shrink-0">
                        <div
                            className="h-full w-full"
                            style={{ backgroundColor: current }}
                        />
                        <input
                            type="color"
                            value={current}
                            onChange={(e) => {
                                const next = normalizeHex(
                                    e.target.value,
                                    current
                                );
                                setLocal(next);
                                onApply(next);
                            }}
                            className="absolute inset-0 h-full w-full cursor-pointer opacity-0 focus:outline-none"
                            aria-label={`${label} color picker`}
                        />
                    </div>

                    {/* hex input */}
                    <input
                        type="text"
                        spellCheck={false}
                        placeholder="#000000"
                        className="flex-1 border-0 bg-transparent px-2 text-sm leading-6 text-neutral-800 placeholder:text-neutral-400 focus:outline-none"
                        value={local}
                        onChange={(e) => setLocal(e.target.value)}
                        onBlur={commitIfChanged}
                        onKeyDown={(e) =>
                            e.key === 'Enter' && e.currentTarget.blur()
                        }
                        aria-label={`${label} hex input`}
                    />

                    {busy ? (
                        <div className="flex items-center pr-2">
                            <LoadingSpinner size="small" />
                        </div>
                    ) : null}
                </div>
            </div>

            {below ? <div className="mt-1">{below}</div> : null}
        </div>
    );
}

/* ---------------- Compact palette strip (no tooltip) ---------------- */
function PaletteStrip({ variant, refreshKey }) {
    const [colors, setColors] = useState(() => readVariantFromCSS(variant));

    useEffect(() => {
        const id = setTimeout(() => setColors(readVariantFromCSS(variant)), 10);
        return () => clearTimeout(id);
    }, [variant, refreshKey]);

    return (
        <div className="rounded-xl ring-1 ring-neutral-200">
            <div className="flex h-4 w-full overflow-hidden rounded-xl">
                {SHADES.map((s, i) => {
                    const hex = colors[s] || '#E5E7EB';
                    return (
                        <div
                            key={s}
                            className={`flex-1 ${
                                i === 0 ? 'rounded-l-xl' : ''
                            } ${i === SHADES.length - 1 ? 'rounded-r-xl' : ''}`}
                            style={{ backgroundColor: hex }}
                            aria-label={`${variant}-${s} • ${hex}`}
                            title={`${variant}-${s} • ${hex}`}
                        />
                    );
                })}
            </div>
        </div>
    );
}

/* ---------------- button color chips + one live preview button (hover) ---------------- */
function ButtonColorChipsRow({
    variant,
    label,
    selectedShade,
    onSelect,
    hexMap,
}) {
    return (
        <div className="flex items-center gap-3">
            <div className="w-10 shrink-0 text-[11px] font-medium leading-5 text-neutral-500">
                {label}
            </div>
            <div className="flex min-w-0 flex-wrap gap-1.5">
                {SHADES.map((s) => {
                    const hex = hexMap?.[`${variant}-${s}`] || '#E5E7EB';
                    const active = selectedShade === s;
                    return (
                        <button
                            key={s}
                            type="button"
                            onClick={() => onSelect(s)}
                            className={`h-3 w-3 rounded-xl ring-1 ring-inset transition ${
                                active
                                    ? 'scale-[1.06] ring-black/70'
                                    : 'ring-black/10 hover:ring-black/25'
                            }`}
                            style={{ backgroundColor: hex }}
                            aria-label={`${variant} ${label} ${s}`}
                            title={`${variant}-${s} ${hex}`}
                        />
                    );
                })}
            </div>
        </div>
    );
}

function AngleStepControl({ label, value, onStep }) {
    return (
        <div className="mb-2 flex items-center gap-2">
            <span className="text-[11px] font-medium leading-5 text-neutral-500">
                {label}
            </span>
            <div className="flex items-center gap-1.5">
                <button
                    type="button"
                    onClick={() => onStep(-15)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-neutral-200 bg-white text-sm text-neutral-700 transition hover:bg-neutral-50"
                    aria-label={`${label} rotate left`}
                    title="Rotate left"
                >
                    ↺
                </button>
                <div className="inline-flex h-7 min-w-[54px] items-center justify-center rounded-md border border-neutral-200 bg-white px-2 text-xs font-medium leading-5 text-neutral-700">
                    {normalizeAngleValue(value)}°
                </div>
                <button
                    type="button"
                    onClick={() => onStep(15)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-neutral-200 bg-white text-sm text-neutral-700 transition hover:bg-neutral-50"
                    aria-label={`${label} rotate right`}
                    title="Rotate right"
                >
                    ↻
                </button>
            </div>
        </div>
    );
}

function SitePanelDropdown({
    value,
    onChange,
    options = [],
    disabled = false,
    leading = null,
    leadingClassName = 'mr-2 inline-flex items-center gap-2',
    buttonClassName = 'relative flex h-9 w-full cursor-default items-center rounded-[10px] bg-white px-3 pr-8 text-left text-xs leading-5 text-neutral-800 focus:outline-none md:text-sm',
}) {
    const normalizedOptions = Array.isArray(options) ? options : [];
    const values = normalizedOptions.map((opt) => opt.value);
    const normalized = values.includes(value) ? value : values[0];

    useEffect(() => {
        if (!normalizedOptions.length) return;
        if (value !== normalized) onChange?.(normalized);
    }, [normalizedOptions.length, value, normalized, onChange]);

    if (!normalizedOptions.length) return null;

    return (
        <Listbox value={normalized} onChange={onChange} disabled={disabled}>
            <div className="relative w-full">
                <Listbox.Button
                    className={`${buttonClassName} ${
                        disabled ? 'opacity-60' : ''
                    }`}
                >
                    {leading ? (
                        <span className={leadingClassName}>{leading}</span>
                    ) : null}
                    <span className="truncate">
                        {normalizedOptions.find(
                            (opt) => opt.value === normalized
                        )?.label || ''}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                        <ChevronDownIcon className="h-4 w-4 text-neutral-500" />
                    </span>
                </Listbox.Button>

                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                >
                    <Listbox.Options className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-neutral-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none">
                        {normalizedOptions.map((option) => (
                            <Listbox.Option
                                key={option.value}
                                value={option.value}
                                className={({ active, selected }) =>
                                    [
                                        'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                        active
                                            ? 'bg-primary-50 text-primary-700'
                                            : 'text-neutral-800',
                                        selected ? 'font-medium' : '',
                                    ].join(' ')
                                }
                            >
                                {({ selected }) => (
                                    <>
                                        <span className="truncate">
                                            {option.label}
                                        </span>
                                        {selected && (
                                            <CheckIcon className="h-4 w-4 text-primary-600" />
                                        )}
                                    </>
                                )}
                            </Listbox.Option>
                        ))}
                    </Listbox.Options>
                </Transition>
            </div>
        </Listbox>
    );
}

function ButtonColorControls({
    variant,
    onChange,
    onGradientApply,
    onBorderApply,
    onIconChange,
    onIconPositionChange,
    onIconSizeChange,
    onShadowChange,
    onHoverAnimationChange,
    borderWidth,
    siteColors,
    buttonColors,
    hideTitle = false,
}) {
    const [defShade, setDefShade] = useState('700');
    const [hoverShade, setHoverShade] = useState('600');
    const [gradientState, setGradientState] = useState(
        BUTTON_GRADIENT_STATES[0].key
    );
    const [gradientAngle, setGradientAngle] = useState(45);
    const [gradientColors, setGradientColors] = useState(['#1B7CE4']);
    const [gradientText, setGradientText] = useState('#FFFFFF');
    const [gradientOpacity, setGradientOpacity] = useState(100);
    const [borderAngles, setBorderAngles] = useState({
        default: 45,
        hover: 45,
    });
    const [borderColorsByState, setBorderColorsByState] = useState({
        default: [variant === 'secondary' ? '#1AB261' : '#1B7CE4'],
        hover: [variant === 'secondary' ? '#1ECD6F' : '#569EEB'],
    });
    const [borderOpacityByState, setBorderOpacityByState] = useState({
        default: 100,
        hover: 100,
    });
    const gradientSyncSignatureRef = useRef({ default: '', hover: '' });
    const borderSyncSignatureRef = useRef({ default: '', hover: '' });
    const getPresetBorderHex = (shade) => {
        const fallback = variant === 'secondary' ? '#1AB261' : '#1B7CE4';
        return normalizeHex(siteColors?.[`${variant}-${shade}`], fallback);
    };
    const applyPresetForState = (state, shade) => {
        onChange({
            variant,
            state,
            key: `${variant}-${shade}`,
        });
        const nextBorderHex = getPresetBorderHex(shade);
        const nextBorderToken = `var(--${variant}-${shade})`;
        if (state === gradientState) {
            setGradientOpacity(100);
        }
        setBorderOpacityByState((prev) => ({ ...prev, [state]: 100 }));
        setBorderColorsByState((prev) => ({
            ...prev,
            [state]: [nextBorderHex],
        }));
        setBorderAngles((prev) => ({
            ...prev,
            [state]: 45,
        }));
        onBorderApply({
            variant,
            state,
            width: borderWidth,
            color: nextBorderToken,
            angle: 45,
        });
    };

    useEffect(() => {
        const nextDefaultShade = inferPaletteShadeFromButtonState({
            variant,
            state: 'default',
            siteColors,
            buttonColors,
        });
        const nextHoverShade = inferPaletteShadeFromButtonState({
            variant,
            state: 'hover',
            siteColors,
            buttonColors,
        });
        setDefShade(nextDefaultShade);
        setHoverShade(nextHoverShade);
    }, [
        variant,
        siteColors,
        buttonColors?.[`${variant}-default`],
        buttonColors?.[`${variant}-default-solid`],
        buttonColors?.[`${variant}-hover`],
        buttonColors?.[`${variant}-hover-solid`],
    ]);
    const activeButtonBg = buttonColors?.[`${variant}-${gradientState}`] || '';
    const activeButtonText =
        buttonColors?.[`text-${variant}-${gradientState}`] || '';
    const defaultBorderRaw =
        buttonColors?.[`${variant}-border-default`] ||
        buttonColors?.[`${variant}-border-color`] ||
        '';
    const hoverBorderRaw =
        buttonColors?.[`${variant}-border-hover`] || defaultBorderRaw || '';
    const activeBorderRaw =
        gradientState === 'hover' ? hoverBorderRaw : defaultBorderRaw;
    const defaultThemeShadeHex = normalizeHex(
        siteColors?.[`${variant}-${defShade}`],
        variant === 'secondary' ? '#1AB261' : '#1B7CE4'
    );
    const hoverThemeShadeHex = normalizeHex(
        siteColors?.[`${variant}-${hoverShade}`],
        variant === 'secondary' ? '#1AB261' : '#1B7CE4'
    );

    useEffect(() => {
        let nextAngle = 45;
        let nextColors = ['#1B7CE4'];
        let nextOpacity = 100;
        const parsed = parseLinearGradient(activeButtonBg);
        if (parsed) {
            const parsedStops = parsed.colors
                .map((entry) =>
                    splitHexAndOpacity(
                        entry,
                        variant === 'secondary' ? '#1AB261' : '#1B7CE4'
                    )
                )
                .filter((entry) => !!entry.color)
                .slice(0, 5);
            const normalizedStops = parsedStops
                .map((entry) => entry.color)
                .filter(Boolean);
            const dedupedStops =
                normalizedStops.length > 1 &&
                normalizedStops.every((entry) => entry === normalizedStops[0])
                    ? [normalizedStops[0]]
                    : normalizedStops;
            nextAngle = Math.round(Math.max(0, Math.min(360, parsed.angle)));
            nextOpacity = parsedStops.length > 0 ? parsedStops[0].opacity : 100;
            nextColors = dedupedStops.length ? dedupedStops : ['#1B7CE4'];
        } else {
            const stateBase =
                gradientState === 'hover'
                    ? hoverThemeShadeHex
                    : defaultThemeShadeHex;
            nextColors = [normalizeHex(stateBase, '#1B7CE4')];
        }
        const nextText = normalizeCssColorToHex(activeButtonText, '#FFFFFF');
        const gradientSignature = [
            nextAngle,
            nextOpacity,
            nextText,
            ...(nextColors || []),
        ].join('|');
        if (
            gradientSyncSignatureRef.current[gradientState] !==
            gradientSignature
        ) {
            gradientSyncSignatureRef.current[gradientState] = gradientSignature;
            setGradientAngle((prev) => (prev === nextAngle ? prev : nextAngle));
            setGradientColors((prev) =>
                arraysShallowEqual(prev, nextColors) ? prev : nextColors
            );
            setGradientOpacity((prev) =>
                prev === nextOpacity ? prev : nextOpacity
            );
        }
        setGradientText((prev) => (prev === nextText ? prev : nextText));
    }, [
        activeButtonBg,
        activeButtonText,
        defaultThemeShadeHex,
        hoverThemeShadeHex,
        gradientState,
    ]);

    useEffect(() => {
        const fallback =
            gradientState === 'hover'
                ? hoverThemeShadeHex
                : defaultThemeShadeHex;
        let nextAngle = 45;
        let nextOpacity = 100;
        let nextColors = [fallback];

        const parsed = parseLinearGradient(activeBorderRaw);
        if (parsed) {
            const parsedStops = parsed.colors
                .map((entry) => splitHexAndOpacity(entry, fallback))
                .filter((entry) => !!entry.color)
                .slice(0, 5);
            const normalizedStops = parsedStops
                .map((entry) => entry.color)
                .filter(Boolean);
            const dedupedStops =
                normalizedStops.length > 1 &&
                normalizedStops.every((entry) => entry === normalizedStops[0])
                    ? [normalizedStops[0]]
                    : normalizedStops;
            nextAngle = Math.round(Math.max(0, Math.min(360, parsed.angle)));
            nextOpacity = parsedStops.length > 0 ? parsedStops[0].opacity : 100;
            nextColors = dedupedStops.length ? dedupedStops : [fallback];
        } else {
            nextColors = [
                splitHexAndOpacity(activeBorderRaw, fallback).color || fallback,
            ];
            nextOpacity = splitHexAndOpacity(activeBorderRaw, fallback).opacity;
        }
        const borderSignature = [nextAngle, nextOpacity, ...nextColors].join(
            '|'
        );
        if (borderSyncSignatureRef.current[gradientState] === borderSignature) {
            return;
        }
        borderSyncSignatureRef.current[gradientState] = borderSignature;
        setBorderAngles((prev) => {
            if ((prev[gradientState] ?? 45) === nextAngle) return prev;
            return { ...prev, [gradientState]: nextAngle };
        });
        setBorderOpacityByState((prev) => {
            if ((prev[gradientState] ?? 100) === nextOpacity) return prev;
            return { ...prev, [gradientState]: nextOpacity };
        });
        setBorderColorsByState((prev) => {
            const current = prev[gradientState] || [];
            if (arraysShallowEqual(current, nextColors)) return prev;
            return { ...prev, [gradientState]: nextColors };
        });
    }, [
        activeBorderRaw,
        defaultThemeShadeHex,
        gradientState,
        hoverThemeShadeHex,
    ]);

    const activeBorderAngle = borderAngles[gradientState] ?? 45;
    const activeBorderColors = borderColorsByState[gradientState] || [
        variant === 'secondary' ? '#1AB261' : '#1B7CE4',
    ];
    const activeBorderOpacity = borderOpacityByState[gradientState] ?? 100;
    const activeBorderPickerFallback = normalizeHex(
        siteColors?.[
            `${variant}-${gradientState === 'hover' ? hoverShade : defShade}`
        ],
        variant === 'secondary' ? '#1AB261' : '#1B7CE4'
    );
    const activeShadowValue =
        buttonColors?.[`${variant}-shadow-${gradientState}`] || 'none';
    const activeShadowMode =
        String(activeShadowValue).trim().toLowerCase() === 'none'
            ? 'none'
            : 'shadow';
    const activeHoverAnimation = normalizeButtonHoverAnimation(
        buttonColors?.[`${variant}-hover-animation`]
    );
    const activeIconValueRaw =
        buttonColors?.[`${variant}-icon-${gradientState}`];
    const activeIconValue =
        typeof activeIconValueRaw === 'string' &&
        activeIconValueRaw.trim().toLowerCase() !== 'none'
            ? activeIconValueRaw.trim()
            : '';
    const activeIconPosition =
        String(buttonColors?.[`${variant}-icon-position`] || 'right')
            .trim()
            .toLowerCase() === 'left'
            ? 'left'
            : 'right';
    const activeIconSizeRaw = String(
        buttonColors?.[`${variant}-icon-size`] || 'medium'
    )
        .trim()
        .toLowerCase();
    const activeIconSize = BUTTON_ICON_SIZE_OPTIONS.some(
        (option) => option.key === activeIconSizeRaw
    )
        ? activeIconSizeRaw
        : 'medium';

    const emitGradient = ({
        nextColors = gradientColors,
        nextAngle = gradientAngle,
        nextText = gradientText,
        nextOpacity = gradientOpacity,
    } = {}) => {
        const stops = nextColors
            .map((entry) => normalizeHex(entry, null))
            .filter((entry) => !!entry)
            .map((entry) => applyOpacityToColor(entry, nextOpacity));
        if (!stops.length) return;
        const resolvedStops = stops.length === 1 ? [stops[0], stops[0]] : stops;
        onGradientApply({
            variant,
            state: gradientState,
            angle: nextAngle,
            colors: resolvedStops,
            textColor: nextText,
        });
    };

    const emitBorder = ({
        nextColors = activeBorderColors,
        nextAngle = activeBorderAngle,
        nextOpacity = activeBorderOpacity,
    } = {}) => {
        const stops = nextColors
            .map((entry) => normalizeHex(entry, null))
            .filter((entry) => !!entry)
            .map((entry) => applyOpacityToColor(entry, nextOpacity));
        if (!stops.length) return;
        if (stops.length >= 2) {
            onBorderApply({
                variant,
                state: gradientState,
                width: borderWidth,
                colors: stops,
                angle: nextAngle,
            });
            return;
        }
        onBorderApply({
            variant,
            state: gradientState,
            width: borderWidth,
            color: stops[0],
            angle: nextAngle,
        });
    };

    const updateGradientColor = (index, nextValue) => {
        setGradientColors((prev) => {
            const next = prev.slice();
            next[index] = nextValue;
            emitGradient({ nextColors: next });
            return next;
        });
    };

    const addGradientColor = () => {
        setGradientColors((prev) => {
            if (prev.length >= 5) return prev;
            const suggestion =
                pickSuggestedThemeColor({
                    baseColor: prev[0],
                    siteColors,
                    existingColors: prev,
                }) || (variant === 'secondary' ? '#1AB261' : '#1B7CE4');
            const next = [...prev, suggestion];
            emitGradient({ nextColors: next });
            return next;
        });
    };

    const rotateGradientAngle = (delta) => {
        const nextAngle = normalizeAngleValue(gradientAngle + delta);
        setGradientAngle(nextAngle);
        emitGradient({ nextAngle });
    };

    const removeGradientColor = (index) => {
        setGradientColors((prev) => {
            if (prev.length <= 1) return prev;
            const next = prev.filter((_, idx) => idx !== index);
            emitGradient({ nextColors: next });
            return next;
        });
    };

    const updateBorderColor = (index, nextValue) => {
        setBorderColorsByState((prev) => {
            const stateColors = prev[gradientState] || [];
            const next = stateColors.slice();
            next[index] = nextValue;
            emitBorder({ nextColors: next });
            return { ...prev, [gradientState]: next };
        });
    };

    const addBorderColor = () => {
        const fallback =
            siteColors?.[
                `${variant}-${
                    gradientState === 'hover' ? hoverShade : defShade
                }`
            ] || (variant === 'secondary' ? '#1AB261' : '#1B7CE4');
        setBorderColorsByState((prev) => {
            const stateColors = prev[gradientState] || [];
            if (stateColors.length >= 5) return prev;
            const baseColor =
                normalizeHex(stateColors[0], null) ||
                normalizeHex(fallback, fallback);
            const suggestion =
                pickSuggestedThemeColor({
                    baseColor,
                    siteColors,
                    existingColors: stateColors,
                }) || normalizeHex(fallback, fallback);
            const nextStateColors = [...stateColors, suggestion];
            emitBorder({ nextColors: nextStateColors });
            return {
                ...prev,
                [gradientState]: nextStateColors,
            };
        });
    };

    const removeBorderColor = (index) => {
        setBorderColorsByState((prev) => {
            const stateColors = prev[gradientState] || [];
            if (stateColors.length <= 1) {
                setBorderOpacityByState((opacityPrev) => ({
                    ...opacityPrev,
                    [gradientState]: 0,
                }));
                emitBorder({
                    nextColors: ['transparent'],
                    nextOpacity: 0,
                });
                const nextStateColors = ['transparent'];
                return arraysShallowEqual(stateColors, nextStateColors)
                    ? prev
                    : {
                          ...prev,
                          [gradientState]: nextStateColors,
                      };
            }
            const nextStateColors = stateColors.filter(
                (_, idx) => idx !== index
            );
            emitBorder({ nextColors: nextStateColors });
            return {
                ...prev,
                [gradientState]: nextStateColors,
            };
        });
    };

    const rotateBorderAngle = (delta) => {
        const nextAngle = normalizeAngleValue(activeBorderAngle + delta);
        setBorderAngles((prev) => ({ ...prev, [gradientState]: nextAngle }));
        emitBorder({ nextAngle });
    };

    const updateGradientOpacity = (nextValue) => {
        const nextOpacity = clampOpacityPercentFromSlider(nextValue);
        setGradientOpacity(nextOpacity);
        emitGradient({ nextOpacity });
    };

    const updateBorderOpacity = (nextValue) => {
        const nextOpacity = clampOpacityPercentFromSlider(nextValue);
        setBorderOpacityByState((prev) => ({
            ...prev,
            [gradientState]: nextOpacity,
        }));
        emitBorder({ nextOpacity });
    };

    return (
        <div className="rounded-xl bg-neutral-300 p-[2px]">
            <div className="rounded-[10px] bg-white p-3">
                {!hideTitle ? (
                    <div className="mb-2 text-[11px] font-bold leading-5 text-primary-700">
                        {variant.charAt(0).toUpperCase() + variant.slice(1)}{' '}
                        Button
                    </div>
                ) : null}

                <div className="mt-3 rounded-xl bg-neutral-100 p-2 pb-3">
                    <div className="mb-2 text-[11px] font-semibold leading-5 text-neutral-600">
                        Preset Colors
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                        <ButtonColorChipsRow
                            variant={variant}
                            label="Default"
                            selectedShade={defShade}
                            onSelect={(s) => {
                                setDefShade(s);
                                applyPresetForState('default', s);
                            }}
                            hexMap={siteColors}
                        />
                        <ButtonColorChipsRow
                            variant={variant}
                            label="Hover"
                            selectedShade={hoverShade}
                            onSelect={(s) => {
                                setHoverShade(s);
                                applyPresetForState('hover', s);
                            }}
                            hexMap={siteColors}
                        />
                    </div>
                </div>

                <div className="mt-3 rounded-xl bg-neutral-100 p-2">
                    <div className="mb-2 flex items-center justify-between">
                        <span className="text-[11px] font-semibold leading-5 text-neutral-600">
                            Custom Colors
                        </span>
                        <div className="w-[124px] rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                            <SitePanelDropdown
                                value={gradientState}
                                onChange={setGradientState}
                                options={BUTTON_GRADIENT_STATES.map((opt) => ({
                                    value: opt.key,
                                    label: opt.label,
                                }))}
                                buttonClassName="relative flex h-7 w-full cursor-default items-center rounded-[10px] bg-white px-2 pr-7 text-left text-xs leading-5 text-neutral-800 focus:outline-none"
                            />
                        </div>
                    </div>

                    <div className="mb-2 flex flex-wrap items-center gap-2">
                        {gradientColors.map((val, idx) => (
                            <div
                                key={`${variant}-${gradientState}-${idx}`}
                                className="flex h-[38px] min-w-[92px] items-center gap-2 rounded-[10px] border border-neutral-200 bg-white px-2"
                            >
                                <input
                                    type="color"
                                    value={normalizeHex(val, '#000000')}
                                    onChange={(e) =>
                                        updateGradientColor(idx, e.target.value)
                                    }
                                    onInput={(e) =>
                                        updateGradientColor(idx, e.target.value)
                                    }
                                    className="h-7 w-7 cursor-pointer rounded-md border-0 bg-transparent p-0"
                                    aria-label={`${variant} gradient color ${
                                        idx + 1
                                    }`}
                                />
                                {gradientColors.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeGradientColor(idx)}
                                        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-sm font-semibold text-neutral-400 ring-1 ring-neutral-200 transition hover:text-red-600 hover:ring-red-300"
                                        aria-label={`Remove ${variant} gradient color ${
                                            idx + 1
                                        }`}
                                        title="Remove"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        ))}

                        {gradientColors.length < 5 && (
                            <button
                                type="button"
                                onClick={addGradientColor}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-dashed border-neutral-400 text-lg text-neutral-600 transition hover:border-neutral-600 hover:text-neutral-800"
                                aria-label={`Add ${variant} gradient color`}
                                title="Add color"
                            >
                                +
                            </button>
                        )}
                    </div>

                    <div className="mb-2">
                        <div className="mb-1 text-[11px] font-medium leading-5 text-neutral-500">
                            Fill Transparency
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="range"
                                min={1}
                                max={100}
                                value={gradientOpacity}
                                onChange={(e) =>
                                    updateGradientOpacity(e.target.value)
                                }
                                className="tw-slider flex-1 cursor-pointer"
                                aria-label={`${variant} ${gradientState} fill transparency`}
                            />
                            <span className="w-10 text-right text-[11px] font-medium leading-5 text-neutral-600">
                                {gradientOpacity}%
                            </span>
                        </div>
                    </div>

                    <AngleStepControl
                        label="Angle"
                        value={gradientAngle}
                        onStep={rotateGradientAngle}
                    />

                    <div className="mb-2 flex items-center gap-2">
                        <span className="text-[11px] font-medium leading-5 text-neutral-500">
                            Text
                        </span>
                        <input
                            type="color"
                            value={normalizeHex(gradientText, '#FFFFFF')}
                            onChange={(e) => {
                                const nextText = e.target.value;
                                setGradientText(nextText);
                                emitGradient({ nextText });
                            }}
                            onInput={(e) => {
                                const nextText = e.target.value;
                                setGradientText(nextText);
                                emitGradient({ nextText });
                            }}
                            className="h-7 w-10 cursor-pointer rounded-md border border-neutral-200 bg-white p-0"
                            aria-label={`${variant} gradient text color`}
                        />
                    </div>

                    <div className="mb-2">
                        <div className="mb-1 text-[11px] font-medium leading-5 text-neutral-500">
                            Icon
                        </div>
                        <div className="mb-2 flex items-center justify-between">
                            <span className="text-[11px] font-medium leading-5 text-neutral-500">
                                Position
                            </span>
                            <div className="w-[104px] rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                <SitePanelDropdown
                                    value={activeIconPosition}
                                    onChange={(nextPosition) =>
                                        onIconPositionChange?.({
                                            variant,
                                            position: nextPosition,
                                        })
                                    }
                                    options={BUTTON_ICON_POSITION_OPTIONS.map(
                                        (option) => ({
                                            value: option.key,
                                            label: option.label,
                                        })
                                    )}
                                    buttonClassName="relative flex h-7 w-full cursor-default items-center rounded-[10px] bg-white px-2 pr-7 text-left text-xs leading-5 text-neutral-800 focus:outline-none"
                                />
                            </div>
                        </div>
                        <div className="mb-2 flex items-center justify-between">
                            <span className="text-[11px] font-medium leading-5 text-neutral-500">
                                Size
                            </span>
                            <div className="w-[104px] rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                <SitePanelDropdown
                                    value={activeIconSize}
                                    onChange={(nextSize) =>
                                        onIconSizeChange?.({
                                            variant,
                                            size: nextSize,
                                        })
                                    }
                                    options={BUTTON_ICON_SIZE_OPTIONS.map(
                                        (option) => ({
                                            value: option.key,
                                            label: option.label,
                                        })
                                    )}
                                    buttonClassName="relative flex h-7 w-full cursor-default items-center rounded-[10px] bg-white px-2 pr-7 text-left text-xs leading-5 text-neutral-800 focus:outline-none"
                                />
                            </div>
                        </div>
                        <FontAwesomeIconPicker
                            value={activeIconValue}
                            onChange={(nextIcon) =>
                                onIconChange?.({
                                    variant,
                                    state: gradientState,
                                    icon: nextIcon,
                                })
                            }
                            placeholder={`Search ${variant} button icons...`}
                        />
                    </div>

                    <div className="mt-3 border-t border-neutral-200 pt-3">
                        <div className="mb-2 text-[11px] font-semibold leading-5 text-neutral-600">
                            Border Color
                        </div>

                        <div className="mb-2 flex flex-wrap items-center gap-2">
                            {activeBorderColors.map((color, idx) => (
                                <div
                                    key={`${variant}-border-${gradientState}-${idx}`}
                                    className="flex h-[38px] min-w-[92px] items-center gap-2 rounded-[10px] border border-neutral-200 bg-white px-2"
                                >
                                    <input
                                        type="color"
                                        value={
                                            normalizeHex(
                                                color === 'transparent'
                                                    ? activeBorderPickerFallback
                                                    : color,
                                                activeBorderPickerFallback
                                            ) || '#000000'
                                        }
                                        onChange={(e) =>
                                            updateBorderColor(
                                                idx,
                                                e.target.value
                                            )
                                        }
                                        onInput={(e) =>
                                            updateBorderColor(
                                                idx,
                                                e.target.value
                                            )
                                        }
                                        className="h-7 w-7 cursor-pointer rounded-md border-0 bg-transparent p-0"
                                        aria-label={`${variant} ${gradientState} border color ${
                                            idx + 1
                                        }`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeBorderColor(idx)}
                                        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-sm font-semibold text-neutral-400 ring-1 ring-neutral-200 transition hover:text-red-600 hover:ring-red-300"
                                        aria-label={`Clear ${variant} ${gradientState} border color ${
                                            idx + 1
                                        }`}
                                        title={
                                            activeBorderColors.length > 1
                                                ? 'Remove'
                                                : 'Clear (transparent)'
                                        }
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}

                            {activeBorderColors.length < 5 && (
                                <button
                                    type="button"
                                    onClick={addBorderColor}
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-dashed border-neutral-400 text-lg text-neutral-600 transition hover:border-neutral-600 hover:text-neutral-800"
                                    aria-label={`Add ${variant} ${gradientState} border color`}
                                    title="Add color"
                                >
                                    +
                                </button>
                            )}
                        </div>

                        <div className="mb-2">
                            <div className="mb-1 text-[11px] font-medium leading-5 text-neutral-500">
                                Border Transparency
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="range"
                                    min={1}
                                    max={100}
                                    value={activeBorderOpacity}
                                    onChange={(e) =>
                                        updateBorderOpacity(e.target.value)
                                    }
                                    className="tw-slider flex-1 cursor-pointer"
                                    aria-label={`${variant} ${gradientState} border transparency`}
                                />
                                <span className="w-10 text-right text-[11px] font-medium leading-5 text-neutral-600">
                                    {activeBorderOpacity}%
                                </span>
                            </div>
                        </div>

                        <AngleStepControl
                            label="Angle"
                            value={activeBorderAngle}
                            onStep={rotateBorderAngle}
                        />

                        <div className="mt-3 flex items-center justify-between">
                            <span className="text-[11px] font-semibold leading-5 text-primary-700">
                                Shadow
                            </span>
                            <button
                                type="button"
                                className="group relative inline-flex cursor-pointer items-center focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-300"
                                role="switch"
                                aria-checked={activeShadowMode === 'shadow'}
                                aria-label={`${variant} ${gradientState} shadow toggle`}
                                onClick={() =>
                                    onShadowChange?.({
                                        variant,
                                        state: gradientState,
                                        mode:
                                            activeShadowMode === 'shadow'
                                                ? 'none'
                                                : 'shadow',
                                    })
                                }
                            >
                                <div
                                    className={`h-5 w-9 rounded-full transition-colors after:absolute after:left-[3px] after:top-0.5 after:h-4 after:w-4 after:rounded-full after:border after:bg-white after:transition-all after:content-[''] ${
                                        activeShadowMode === 'shadow'
                                            ? 'bg-primary-600 after:translate-x-4 after:border-white'
                                            : 'bg-neutral-300 after:border-gray-300'
                                    }`}
                                    aria-hidden="true"
                                />
                            </button>
                        </div>

                        <div className="mt-3">
                            <div className="mb-1 text-[11px] font-semibold leading-5 text-neutral-600">
                                Hover Animation
                            </div>
                            <div className="w-[132px] rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                <SitePanelDropdown
                                    value={activeHoverAnimation}
                                    onChange={(nextAnimation) =>
                                        onHoverAnimationChange?.({
                                            variant,
                                            animation: nextAnimation,
                                        })
                                    }
                                    options={BUTTON_HOVER_ANIMATION_OPTIONS.map(
                                        (option) => ({
                                            value: option.key,
                                            label: option.label,
                                        })
                                    )}
                                    buttonClassName="relative flex h-7 w-full cursor-default items-center rounded-[10px] bg-white px-2 pr-7 text-left text-xs leading-5 text-neutral-800 focus:outline-none"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ButtonPresetPreview({ variant, siteColors, buttonColors }) {
    const defaultShade =
        inferPaletteShadeFromButtonState({
            variant,
            state: 'default',
            siteColors,
            buttonColors,
        }) || (variant === 'secondary' ? '700' : '700');
    const hoverShade =
        inferPaletteShadeFromButtonState({
            variant,
            state: 'hover',
            siteColors,
            buttonColors,
        }) || (variant === 'secondary' ? '600' : '600');
    const defaultHex = normalizeHex(
        siteColors?.[`${variant}-${defaultShade}`],
        variant === 'secondary' ? '#1AB261' : '#1B7CE4'
    );
    const hoverHex = normalizeHex(
        siteColors?.[`${variant}-${hoverShade}`],
        variant === 'secondary' ? '#1ECD6F' : '#569EEB'
    );

    return (
        <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2.5 py-1 text-[11px] font-semibold text-neutral-600">
                <span
                    className="h-2.5 w-2.5 rounded-full ring-1 ring-black/10"
                    style={{ backgroundColor: defaultHex }}
                />
                Default
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2.5 py-1 text-[11px] font-semibold text-neutral-600">
                <span
                    className="h-2.5 w-2.5 rounded-full ring-1 ring-black/10"
                    style={{ backgroundColor: hoverHex }}
                />
                Hover
            </span>
        </div>
    );
}

function ButtonPanel({
    variant,
    open,
    onToggle,
    onChange,
    onGradientApply,
    onBorderApply,
    onIconChange,
    onIconPositionChange,
    onIconSizeChange,
    onShadowChange,
    onHoverAnimationChange,
    borderWidth,
    siteColors,
    buttonColors,
}) {
    const title = `${
        variant.charAt(0).toUpperCase() + variant.slice(1)
    } Button`;

    return (
        <div className="rounded-[24px] border border-neutral-200 bg-white shadow-sm">
            <button
                type="button"
                onClick={onToggle}
                className="flex w-full items-center gap-3 rounded-[24px] px-4 py-3 text-left transition-colors hover:bg-neutral-50 focus:outline-none"
            >
                <div className="min-w-0 flex-1">
                    <div className="truncate text-base font-semibold leading-5 text-neutral-900">
                        {title}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <ButtonPresetPreview
                        variant={variant}
                        siteColors={siteColors}
                        buttonColors={buttonColors}
                    />
                    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-500 transition-transform">
                        <ChevronDownIcon
                            className={`h-4 w-4 transition-transform ${
                                open ? 'rotate-180' : ''
                            }`}
                        />
                    </span>
                </div>
            </button>

            {open ? (
                <div className="border-t border-neutral-200 px-4 pb-4 pt-3">
                    <ButtonColorControls
                        variant={variant}
                        onChange={onChange}
                        onGradientApply={onGradientApply}
                        onBorderApply={onBorderApply}
                        onIconChange={onIconChange}
                        onIconPositionChange={onIconPositionChange}
                        onIconSizeChange={onIconSizeChange}
                        onShadowChange={onShadowChange}
                        onHoverAnimationChange={onHoverAnimationChange}
                        borderWidth={borderWidth}
                        siteColors={siteColors}
                        buttonColors={buttonColors}
                        hideTitle
                    />
                </div>
            ) : null}
        </div>
    );
}

/* ---------------- font pickers ---------------- */
const chipInner =
    'inline-flex items-center rounded-[14px] px-2.5 py-1.5 text-[12px] leading-5 bg-white transition';

function FontChip({ family, active, onClick, onRemove }) {
    // Always render chips with a steady preview weight so they don’t “jump”
    const previewWeight = 400;

    return (
        <div
            className={`rounded-2xl p-[1px] transition ${
                active
                    ? 'bg-gradient-to-br from-primary-600 to-secondary-600'
                    : 'bg-transparent'
            }`}
        >
            <div
                className={`${chipInner} ${
                    active
                        ? 'ring-1 ring-transparent'
                        : 'ring-1 ring-neutral-200 hover:ring-neutral-300'
                } gap-1.5`}
            >
                <button
                    type="button"
                    onClick={onClick}
                    className="min-w-0 flex-1 truncate pr-0.5 text-left focus:outline-none"
                    style={{
                        fontFamily: `"${family}"`,
                        fontWeight: previewWeight,
                        color: 'rgb(31 41 55)' /* neutral-800 */,
                    }}
                    title={family}
                >
                    <span className="truncate">{family}</span>
                </button>
                <button
                    type="button"
                    onClick={onRemove}
                    className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold text-neutral-400 ring-1 ring-neutral-200 transition hover:text-red-600 hover:ring-red-300 focus:outline-none"
                    style={{ fontFamily: 'sans-serif' }}
                    title={`Delete ${family}`}
                    aria-label={`Delete ${family}`}
                >
                    ×
                </button>
            </div>
        </div>
    );
}

function WeightChip({ value, active, onClick }) {
    return (
        <button
            type="button"
            onClick={() => onClick(value)}
            className={`h-6 min-w-[34px] rounded-md px-1.5 text-center text-[11px] font-semibold ring-1 ring-inset transition ${
                active
                    ? 'bg-white text-primary-700 ring-primary-500'
                    : 'bg-white text-neutral-800 ring-neutral-200 hover:ring-neutral-300'
            }`}
        >
            {value}
        </button>
    );
}

function weightsForFamily(family, list) {
    const found = list.find((f) => f.font === family);
    return found?.weights?.length ? found.weights : [400];
}

const GOOGLE_FONT_WEIGHT_CACHE = new Map();

const sanitizeFontFamilyInput = (value) =>
    String(value || '')
        .replace(/["'`<>]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const normalizeWeightList = (weights = []) =>
    [
        ...new Set(
            weights.map((weight) => Number(weight)).filter(Number.isFinite)
        ),
    ]
        .map((weight) => Math.round(weight))
        .filter((weight) => weight >= 100 && weight <= 1000)
        .sort((a, b) => a - b);

const parseWeightToken = (token) => {
    const normalized = String(token || '').trim();
    if (!normalized) return [];

    const rangeMatch = normalized.match(/^(\d{3,4})\s+(\d{3,4})$/);
    if (rangeMatch) {
        const start = Number(rangeMatch[1]);
        const end = Number(rangeMatch[2]);
        const low = Math.min(start, end);
        const high = Math.max(start, end);
        const weights = [];
        for (
            let weight = Math.ceil(low / 100) * 100;
            weight <= high;
            weight += 100
        ) {
            weights.push(weight);
        }
        return weights.length ? weights : [Math.round((low + high) / 2)];
    }

    const numeric = Number(normalized);
    if (!Number.isFinite(numeric)) return [];
    return [numeric];
};

const extractWeightsFromGoogleCss = (cssText) => {
    const weights = [];
    const regex = /font-weight:\s*([^;]+);/gi;
    let match;
    while ((match = regex.exec(cssText))) {
        weights.push(...parseWeightToken(match[1]));
    }
    return normalizeWeightList(weights);
};

const fetchGoogleFontWeights = async (family) => {
    const normalizedFamily = sanitizeFontFamilyInput(family);
    if (!normalizedFamily) return [400];
    const cacheKey = normalizedFamily.toLowerCase();
    if (GOOGLE_FONT_WEIGHT_CACHE.has(cacheKey)) {
        return GOOGLE_FONT_WEIGHT_CACHE.get(cacheKey);
    }

    try {
        const response = await fetch(
            `/api/google-font-weights?family=${encodeURIComponent(
                normalizedFamily
            )}`,
            { cache: 'no-store' }
        );
        if (response.ok) {
            const data = await response.json();
            const resolvedWeights = normalizeWeightList(data?.weights || [400]);
            const finalWeights = resolvedWeights.length
                ? resolvedWeights
                : [400];
            GOOGLE_FONT_WEIGHT_CACHE.set(cacheKey, finalWeights);
            return finalWeights;
        }
    } catch {
        // Fallback below
    }

    try {
        const encodedFamily = encodeURIComponent(normalizedFamily).replace(
            /%20/g,
            '+'
        );
        const response = await fetch(
            `https://fonts.googleapis.com/css2?family=${encodedFamily}&display=swap`
        );
        if (!response.ok) {
            throw new Error(`Failed to load font CSS (${response.status})`);
        }
        const css = await response.text();
        const weights = extractWeightsFromGoogleCss(css);
        const resolvedWeights = weights.length ? weights : [400];
        GOOGLE_FONT_WEIGHT_CACHE.set(cacheKey, resolvedWeights);
        return resolvedWeights;
    } catch {
        const fallback = [400];
        GOOGLE_FONT_WEIGHT_CACHE.set(cacheKey, fallback);
        return fallback;
    }
};

const uniqueCustomFonts = (fonts = []) => {
    const seen = new Set();
    return fonts
        .map((font) => sanitizeFontFamilyInput(font))
        .filter((font) => {
            if (!font) return false;
            const key = font.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
};

const normalizeUploadedFonts = (fonts) =>
    (Array.isArray(fonts) ? fonts : [])
        .filter(
            (font) =>
                font &&
                sanitizeFontFamilyInput(font.family) &&
                typeof font.url === 'string' &&
                font.url.startsWith('/fonts/')
        )
        .map((font) => ({
            ...font,
            previewUrl:
                typeof font.previewUrl === 'string' &&
                font.previewUrl.startsWith('data:')
                    ? font.previewUrl
                    : undefined,
        }));

const inferFontFamilyFromFileName = (fileName) =>
    sanitizeFontFamilyInput(
        String(fileName || '')
            .replace(/\.(woff2?|ttf|otf)$/i, '')
            .replace(
                /[-_](thin|extralight|light|regular|medium|semibold|bold|extrabold|black|italic|[1-9]00)$/i,
                ''
            )
            .replace(/[-_]+/g, ' ')
    );

const inferFontWeightFromFileName = (fileName) => {
    const normalized = String(fileName || '').toLowerCase();
    const numeric = normalized.match(/(?:^|[-_])([1-9]00)(?:[-_.]|$)/)?.[1];
    if (numeric) return Number(numeric);
    if (normalized.includes('thin')) return 100;
    if (normalized.includes('extralight')) return 200;
    if (normalized.includes('light')) return 300;
    if (normalized.includes('medium')) return 500;
    if (normalized.includes('semibold')) return 600;
    if (normalized.includes('extrabold')) return 800;
    if (normalized.includes('black')) return 900;
    if (normalized.includes('bold')) return 700;
    return 400;
};

const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Could not read font file'));
        reader.readAsDataURL(file);
    });

const buildFontOptions = ({
    fonts,
    customFonts = [],
    hiddenFonts = [],
    customFontWeights = {},
}) => {
    const hidden = new Set(
        hiddenFonts.map((font) => sanitizeFontFamilyInput(font).toLowerCase())
    );
    const merged = [
        ...(customFonts || []).map((font) => ({ font, weights: [400] })),
        ...fonts,
    ];
    const unique = [];
    const seen = new Set();

    merged.forEach((item) => {
        const normalized = sanitizeFontFamilyInput(item.font);
        if (!normalized) return;
        const key = normalized.toLowerCase();
        if (hidden.has(key) || seen.has(key)) return;
        seen.add(key);
        const weightedItem = customFontWeights[key]
            ? { ...item, weights: customFontWeights[key] }
            : item;
        unique.push({
            ...weightedItem,
            font: normalized,
            weights: normalizeWeightList(weightedItem.weights || [400]),
        });
    });

    return unique;
};

function FontPanel({
    type, // 'display' | 'text'
    label,
    fonts,
    customFonts,
    hiddenFonts = [],
    customFontWeights = {},
    family,
    weight,
    onChangeFamily,
    onChangeWeight,
    onAddCustomFont,
    onUploadFont,
    onRemoveFont,
    open = true,
    onToggle,
    actions = null,
}) {
    const [customFamilyInput, setCustomFamilyInput] = useState('');
    const [addingCustomFont, setAddingCustomFont] = useState(false);
    const [uploadingFont, setUploadingFont] = useState(false);
    const [pendingFontFile, setPendingFontFile] = useState(null);

    const fontOptions = useMemo(() => {
        const unique = buildFontOptions({
            fonts,
            customFonts,
            hiddenFonts,
            customFontWeights,
        });
        const seen = new Set(
            unique.map((item) =>
                sanitizeFontFamilyInput(item.font).toLowerCase()
            )
        );

        const normalizedCurrent = sanitizeFontFamilyInput(family);
        if (!normalizedCurrent) return unique;
        if (
            hiddenFonts
                .map((font) => sanitizeFontFamilyInput(font).toLowerCase())
                .includes(normalizedCurrent.toLowerCase())
        ) {
            return unique;
        }
        if (seen.has(normalizedCurrent.toLowerCase())) return unique;
        return [{ font: normalizedCurrent, weights: [400] }, ...unique];
    }, [customFontWeights, customFonts, family, fonts, hiddenFonts]);

    const applyCustomFamily = async () => {
        const nextFamily = sanitizeFontFamilyInput(customFamilyInput);
        if (!nextFamily) return;
        if (addingCustomFont || uploadingFont) return;

        if (pendingFontFile) {
            setUploadingFont(true);
            try {
                const uploaded = await onUploadFont(
                    pendingFontFile,
                    nextFamily
                );
                if (!uploaded?.family) return;
                onChangeFamily(uploaded.family);
                onChangeWeight(uploaded.weight || 400);
                setPendingFontFile(null);
                setCustomFamilyInput('');
            } finally {
                setUploadingFont(false);
            }
            return;
        }

        try {
            const existsInPreset = fonts.some(
                ({ font }) => font.toLowerCase() === nextFamily.toLowerCase()
            );
            setAddingCustomFont(true);
            const detectedWeights = await onAddCustomFont(nextFamily);

            onChangeFamily(nextFamily);
            if (!existsInPreset) {
                onChangeWeight(detectedWeights?.[0] || 400);
            }
            setCustomFamilyInput('');
        } finally {
            setAddingCustomFont(false);
        }
    };

    const selectFontFile = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || uploadingFont) return;

        setPendingFontFile(file);
        setCustomFamilyInput(inferFontFamilyFromFileName(file.name));
    };

    return (
        <div className="rounded-[24px] border border-neutral-200 bg-white shadow-sm">
            <button
                type="button"
                onClick={onToggle}
                className="flex w-full items-center gap-3 rounded-[24px] px-4 py-3 text-left transition-colors hover:bg-neutral-50 focus:outline-none"
            >
                <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-500">
                        {label ||
                            (type === 'display' ? 'Heading Font' : 'Body Font')}
                    </div>
                    <div
                        className="mt-1 truncate text-base font-semibold leading-5 text-neutral-900"
                        style={{ fontFamily: `"${family}"` }}
                    >
                        {family}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="inline-flex shrink-0 items-center rounded-full bg-neutral-100 px-2.5 py-1 text-[11px] font-semibold text-neutral-600">
                        {weight}
                    </span>
                    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-500 transition-transform">
                        <ChevronDownIcon
                            className={`h-4 w-4 transition-transform ${
                                open ? 'rotate-180' : ''
                            }`}
                        />
                    </span>
                </div>
            </button>

            {open ? (
                <div className="border-t border-neutral-200 px-4 pb-4 pt-3">
                    {actions ? <div className="mb-2">{actions}</div> : null}
                    <div className="mb-2 flex items-center gap-2">
                        <div className="min-w-0 flex-1 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                            <div className="flex h-[30px] items-center gap-1.5 rounded-[10px] bg-white px-2">
                                <input
                                    type="text"
                                    value={customFamilyInput}
                                    onChange={(event) =>
                                        setCustomFamilyInput(event.target.value)
                                    }
                                    onKeyDown={(event) => {
                                        if (event.key !== 'Enter') return;
                                        event.preventDefault();
                                        void applyCustomFamily();
                                    }}
                                    placeholder="Add font name (Google font, or edit custom font name)"
                                    className="min-w-0 flex-1 border-0 bg-transparent text-[11px] text-neutral-700 outline-none focus:border-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                />
                                <button
                                    type="button"
                                    onClick={() => void applyCustomFamily()}
                                    disabled={
                                        addingCustomFont ||
                                        uploadingFont ||
                                        !sanitizeFontFamilyInput(
                                            customFamilyInput
                                        )
                                    }
                                    className="inline-flex h-6 shrink-0 items-center whitespace-nowrap rounded-md bg-primary-700 px-2 text-[10px] font-semibold text-white transition-colors hover:bg-primary-800 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {addingCustomFont || uploadingFont
                                        ? '...'
                                        : 'Add'}
                                </button>
                            </div>
                        </div>
                        <label className="inline-flex h-[34px] shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-2.5 text-[11px] font-semibold text-neutral-700 transition-colors hover:bg-neutral-50">
                            <ArrowUpTrayIcon className="h-3.5 w-3.5" />
                            Upload font file
                            <input
                                type="file"
                                accept=".woff2,.woff,.ttf,.otf,font/woff2,font/woff,font/ttf,font/otf"
                                className="sr-only"
                                disabled={uploadingFont}
                                onChange={selectFontFile}
                            />
                        </label>
                    </div>

                    <div className="max-h-36 overflow-y-auto rounded-[10px] p-3 ring-2 ring-neutral-300">
                        <div className="flex flex-wrap gap-1.5">
                            {fontOptions.map(({ font }) => (
                                <FontChip
                                    key={font}
                                    family={font}
                                    active={
                                        sanitizeFontFamilyInput(
                                            font
                                        ).toLowerCase() ===
                                        sanitizeFontFamilyInput(
                                            family
                                        ).toLowerCase()
                                    }
                                    onClick={() => onChangeFamily(font)}
                                    onRemove={() => onRemoveFont(font)}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {weightsForFamily(family, fontOptions).map((w) => (
                            <WeightChip
                                key={w}
                                value={w}
                                active={String(weight) === String(w)}
                                onClick={onChangeWeight}
                            />
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

/* ============================== SiteSettingsEditor ============================== */
export function SiteSettingsEditor() {
    const editor = useEditor();
    const notifications = useNotifications();
    const { company_id, company_name } = editor.global.data || {};

    const [activeMediaField, setActiveMediaField] = useState(null); // { key, value }
    const [mediaOriginalValue, setMediaOriginalValue] = useState(null);
    const [mediaWasApplied, setMediaWasApplied] = useState(false);
    const settingsScrollRef = useRef(null);
    const mediaScrollRestoreRef = useRef(0);
    const prevActiveMediaRef = useRef(null);

    const [favicon, setFavicon] = useState('');
    const [hasCustomFavicon, setHasCustomFavicon] = useState(false);
    const [saving_favicon, setSavingFavicon] = useState(false);
    const [favicon_saved, setFaviconSaved] = useState(false);
    const [saving_logo, setSavingLogo] = useState(false);
    const [logoAssetPickerOpen, setLogoAssetPickerOpen] = useState(false);
    const [isEditingLogoAlt, setIsEditingLogoAlt] = useState(false);
    const [logoAltDraft, setLogoAltDraft] = useState('');

    const [saved_custom_code, setSavedCustomCode] = useState({
        head: '',
        footer: '',
    });
    const [custom_code, setCustomCode] = useState({ head: '', footer: '' });
    const [getting_tags, setGettingTags] = useState(true);
    const [saving_tags, setSavingTags] = useState(false);
    const [tag_error, setTagError] = useState(false);
    const [llms_generating, setLlmsGenerating] = useState(false);
    const [site_exporting, setSiteExporting] = useState(false);
    const [llmsEditorValue, setLlmsEditorValue] = useState('');
    const [llmsUsesGeneratedDefault, setLlmsUsesGeneratedDefault] =
        useState(false);

    // colors
    const [primaryHex, setPrimaryHex] = useState('#8F52F1');
    const [secondaryHex, setSecondaryHex] = useState('#06B6D4');

    const [paletteHexMap, setPaletteHexMap] = useState({});
    const [paletteVersion, setPaletteVersion] = useState(0);

    // icon overrides (preview first, persist on save)
    const overrideSavedRef = useRef(false);
    const savedPackRef = useRef(
        editor.site.data?.iconPackOverride === 'brands'
            ? 'classic'
            : editor.site.data?.iconPackOverride || 'classic'
    );
    const savedStyleRef = useRef(
        normalizeSiteIconStyle(
            editor.site.data?.iconStyleOverride || 'solid',
            savedPackRef.current
        )
    );
    const [iconPackOverride, setIconPackOverride] = useState(
        savedPackRef.current
    );
    const [iconStyleOverride, setIconStyleOverride] = useState(
        savedStyleRef.current
    );
    const [shapeDividerOverride, setShapeDividerOverride] = useState('tilt');

    // fonts from context
    const headingFamily = editor.site.data?.fonts?.display || 'Inter';
    const bodyFamily = editor.site.data?.fonts?.text || 'Inter';
    const logoTextFontValue = sanitizeFontFamilyInput(
        editor.global.data?.logoTextFont || ''
    );
    const logoTextSizeValue = String(
        editor.global.data?.logoTextSize || ''
    ).trim();
    const headingWeight = editor.site.data?.fontWeights?.display || 700;
    const bodyWeight = editor.site.data?.fontWeights?.text || 400;
    const logoTextFontWeightValue =
        editor.global.data?.logoTextFontWeight || headingWeight || 700;
    const logoTextSizeNumber = Math.max(
        LOGO_TEXT_SIZE_MIN,
        Math.min(
            LOGO_TEXT_SIZE_MAX,
            Number.parseInt(logoTextSizeValue, 10) || LOGO_TEXT_SIZE_DEFAULT
        )
    );
    const navComponent = useMemo(
        () =>
            (editor.components?.data || []).find(
                (component) =>
                    component?.name === 'Nav' ||
                    component?.data_file === 'nav.json'
            ),
        [editor.components?.data]
    );
    const legacyNavLogoText = useMemo(() => {
        return decodeLogoTextEntities(navComponent?.props?.LogoText).trim();
    }, [navComponent?.props?.LogoText]);
    const logoTextOverride = getLogoTextOverride(editor.global.data);
    const logoTextValue = logoTextOverride.hasExplicitValue
        ? logoTextOverride.value
        : legacyNavLogoText;
    const embedCtaLink = coerceBoolean(editor.global.data?.embed_cta_link);
    const hideNicejobPopupFooterText =
        editor.global.data?.hide_nicejob_popup_footer_text === undefined
            ? true
            : coerceBoolean(editor.global.data?.hide_nicejob_popup_footer_text);
    const nicejobPopupFooterFontSize = normalizeNicejobPopupFooterFontSize(
        editor.global.data?.nicejob_popup_footer_font_size
    );
    const [customDisplayFonts, setCustomDisplayFonts] = useState([]);
    const [customTextFonts, setCustomTextFonts] = useState([]);
    const [customFontWeights, setCustomFontWeights] = useState({});
    const [hiddenDisplayFonts, setHiddenDisplayFonts] = useState([]);
    const [hiddenTextFonts, setHiddenTextFonts] = useState([]);
    const [fontPanelOpen, setFontPanelOpen] = useState({
        display: false,
        text: false,
        logo: false,
    });
    const uploadedFonts = useMemo(
        () => normalizeUploadedFonts(editor.global.data?.uploadedFonts),
        [editor.global.data?.uploadedFonts]
    );
    const uploadedFontFamilies = useMemo(
        () => uniqueCustomFonts(uploadedFonts.map((font) => font.family)),
        [uploadedFonts]
    );

    useEffect(() => {
        setCustomDisplayFonts((prev) =>
            uniqueCustomFonts([...uploadedFontFamilies, ...prev])
        );
        setCustomTextFonts((prev) =>
            uniqueCustomFonts([...uploadedFontFamilies, ...prev])
        );
        setCustomFontWeights((prev) => {
            const next = { ...prev };
            uploadedFonts.forEach((font) => {
                const key = sanitizeFontFamilyInput(font.family).toLowerCase();
                next[key] = normalizeWeightList([
                    ...(next[key] || []),
                    font.weight || 400,
                ]);
            });
            return next;
        });
    }, [uploadedFontFamilies, uploadedFonts]);

    const isSiteSettingsOpen = editor.site.visible();
    const [buttonPanelOpen, setButtonPanelOpen] = useState({
        primary: false,
        secondary: false,
    });

    useEffect(() => {
        if (!isSiteSettingsOpen) return;

        const storedLlmsText =
            typeof editor.global.data?.llms_txt === 'string'
                ? editor.global.data.llms_txt
                : '';

        if (storedLlmsText) {
            setLlmsEditorValue(storedLlmsText);
            setLlmsUsesGeneratedDefault(false);
            return;
        }

        let cancelled = false;

        const loadGeneratedLlmsText = async () => {
            try {
                const response = await fetch('/api/llms-txt/generate', {
                    cache: 'no-store',
                });
                if (!response.ok) {
                    throw new Error('Failed to generate llms.txt');
                }
                const data = await response.json();
                if (cancelled) return;
                const nextValue =
                    typeof data?.text === 'string' ? data.text : '';
                setLlmsEditorValue(nextValue);
                setLlmsUsesGeneratedDefault(Boolean(nextValue));
            } catch (error) {
                if (cancelled) return;
                console.error('Failed to preload llms.txt', error);
                setLlmsEditorValue('');
                setLlmsUsesGeneratedDefault(false);
            }
        };

        loadGeneratedLlmsText();

        return () => {
            cancelled = true;
        };
    }, [isSiteSettingsOpen]);

    useEffect(() => {
        if (!isSiteSettingsOpen) return;
        setFontPanelOpen({
            display: false,
            text: false,
            logo: false,
        });
    }, [isSiteSettingsOpen]);

    useEffect(() => {
        const normalized = sanitizeFontFamilyInput(headingFamily);
        if (!normalized) return;
        const existsInPreset = DISPLAY_FONTS.some(
            ({ font }) => font.toLowerCase() === normalized.toLowerCase()
        );
        if (existsInPreset) return;
        setCustomDisplayFonts((prev) =>
            uniqueCustomFonts([normalized, ...prev])
        );
    }, [headingFamily]);

    useEffect(() => {
        const normalized = sanitizeFontFamilyInput(bodyFamily);
        if (!normalized) return;
        const existsInPreset = TEXT_FONTS.some(
            ({ font }) => font.toLowerCase() === normalized.toLowerCase()
        );
        if (existsInPreset) return;
        setCustomTextFonts((prev) => uniqueCustomFonts([normalized, ...prev]));
    }, [bodyFamily]);

    useEffect(() => {
        const wasActive = Boolean(prevActiveMediaRef.current);
        const isActive = Boolean(activeMediaField);
        if (wasActive && !isActive && settingsScrollRef.current) {
            const node = settingsScrollRef.current;
            const maxScroll = Math.max(
                0,
                node.scrollHeight - node.clientHeight
            );
            node.scrollTop = Math.min(mediaScrollRestoreRef.current, maxScroll);
        }
        prevActiveMediaRef.current = activeMediaField;
    }, [activeMediaField]);

    /* ---- init from CSS ---- */
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const root = document.documentElement;
        const cssPrimary =
            root.style.getPropertyValue('--primary-700') ||
            getComputedStyle(root).getPropertyValue('--primary-700');
        const cssSecondary =
            root.style.getPropertyValue('--secondary-700') ||
            getComputedStyle(root).getPropertyValue('--secondary-700');

        if (cssPrimary?.trim())
            setPrimaryHex(normalizeHex(cssPrimary.trim(), '#8F52F1'));
        if (cssSecondary?.trim())
            setSecondaryHex(normalizeHex(cssSecondary.trim(), '#06B6D4'));

        setPaletteHexMap(buildPaletteMap());
    }, []);

    // init icon overrides from site data
    useEffect(() => {
        const storedPack =
            editor.site.data?.iconPackOverride === 'brands'
                ? 'classic'
                : editor.site.data?.iconPackOverride || 'classic';
        const storedStyle = normalizeSiteIconStyle(
            editor.site.data?.iconStyleOverride || 'solid',
            storedPack
        );
        savedPackRef.current = storedPack;
        savedStyleRef.current = storedStyle;
        setIconPackOverride(storedPack);
        setIconStyleOverride(storedStyle);
    }, [
        editor.site.data?.iconPackOverride,
        editor.site.data?.iconStyleOverride,
    ]);

    const shapeDividerComponents = useMemo(
        () =>
            (editor.components?.data || []).filter(
                (component) => component?.name === 'ShapeDivider'
            ),
        [editor.components?.data]
    );
    const currentShapeDividerShape = useMemo(() => {
        const firstShape = shapeDividerComponents[0]?.props?.Shape;
        return normalizeShapeDividerShape(firstShape);
    }, [shapeDividerComponents]);

    useEffect(() => {
        setShapeDividerOverride(currentShapeDividerShape);
    }, [currentShapeDividerShape]);

    /* ---- load custom code ---- */
    useEffect(() => {
        if (!company_id) return;
        const getCustomTags = async () => {
            try {
                const data = await API.get(`${company_id}/custom-code`);
                setCustomCode(data);
                setSavedCustomCode(data);
            } catch (error) {
                console.error(error);
                setTagError(true);
            } finally {
                setGettingTags(false);
            }
        };
        getCustomTags();
    }, [company_id]);

    useEffect(() => {
        if (hasAppliedInitialIconDom.current) return;
        hasAppliedInitialIconDom.current = true;
        applyIconOverrideDom(iconPackOverride, iconStyleOverride);
    }, [iconPackOverride, iconStyleOverride]);

    useEffect(() => {
        iconOverrideRef.current = {
            pack: iconPackOverride,
            style: iconStyleOverride,
        };
        applyIconOverrideDom(iconPackOverride, iconStyleOverride);
        setTimeout(
            () => applyIconOverrideDom(iconPackOverride, iconStyleOverride),
            0
        );
    }, [iconPackOverride, iconStyleOverride]);

    const captureComponentSnapshot = () => {
        const comps = editor.components?.data || [];
        componentSnapshotRef.current =
            comps.map((c) => ({
                file: c.data_file,
                props: structuredClone(c.props),
            })) || [];
    };

    const applyOverrideToComponents = (pack, style) => {
        const comps = editor.components?.data || [];
        if (!componentSnapshotRef.current) {
            captureComponentSnapshot();
        }
        comps.forEach((component) => {
            if (!component?.data_file || !component.props) return;
            const { value: nextProps, changed } = deepRewriteIcons(
                component.props,
                pack,
                style
            );
            const updatedProps = { ...nextProps };
            const shouldForceUpdate =
                component.name === 'Reviews' ||
                component.name === 'ReviewHero' ||
                component.name === 'FloatingAction';
            if (shouldForceUpdate) {
                if (component.name === 'FloatingAction') {
                    updatedProps.IconPackOverride = pack;
                    updatedProps.IconStyleOverride = style;
                } else {
                    updatedProps.FeatureIconPack = pack;
                    updatedProps.FeatureIconStyle = style;
                }
            }
            if (!changed && !shouldForceUpdate) return;
            editor.components.change({
                file: component.data_file,
                value: updatedProps,
            });
        });
    };

    const applyShapeDividerOverrideToComponents = (shape) => {
        const comps = editor.components?.data || [];
        if (!componentSnapshotRef.current) {
            captureComponentSnapshot();
        }
        comps.forEach((component) => {
            if (component?.name !== 'ShapeDivider' || !component.data_file) {
                return;
            }
            editor.components.change({
                file: component.data_file,
                key: 'Shape',
                value: shape,
            });
        });
    };

    /* ---- palette regen flags ---- */
    const applyingPrimary = editor.site.regenerating_palette({
        variant: 'primary',
    });
    const applyingSecondary = editor.site.regenerating_palette({
        variant: 'secondary',
    });

    // when regen finishes, rebuild maps and refresh strips/chips
    useEffect(() => {
        if (applyingPrimary || applyingSecondary) return;
        const id = setTimeout(() => {
            setPaletteHexMap(buildPaletteMap());
            setPaletteVersion((v) => v + 1);
        }, 30);
        return () => clearTimeout(id);
    }, [applyingPrimary, applyingSecondary]);

    /* ---- actions ---- */
    const applyPrimary = (hex) => {
        const h = normalizeHex(hex, primaryHex);
        setPrimaryHex(h);
        editor.site.change({
            mode: 'palette',
            data: { variant: 'primary', color: h },
        });
    };

    const applySecondary = (hex) => {
        const h = normalizeHex(hex, secondaryHex);
        setSecondaryHex(h);
        editor.site.change({
            mode: 'palette',
            data: { variant: 'secondary', color: h },
        });
    };

    const applyButton = ({ variant, state, key, hoverKey }) => {
        editor.site.change({
            mode: 'button-color',
            data: { variant, state, key, hoverKey },
        });
    };

    /* ---- Button sizing ---- */
    const [buttonSizeIndex, setButtonSizeIndex] = useState(
        BUTTON_SIZE_KEY_TO_INDEX.get(
            editor.site.data?.buttonSizing?.preset || 'regular'
        ) ||
            BUTTON_SIZE_KEY_TO_INDEX.get('regular') ||
            2
    );

    useEffect(() => {
        const presetKey = editor.site.data?.buttonSizing?.preset;
        if (!presetKey) return;
        const idx = BUTTON_SIZE_KEY_TO_INDEX.get(presetKey);
        if (typeof idx === 'number') setButtonSizeIndex(idx);
    }, [editor.site.data?.buttonSizing?.preset]);

    const applyButtonSizeIndex = (idx) => {
        const clamped = Math.max(
            0,
            Math.min(BUTTON_SIZE_PRESETS.length - 1, idx | 0)
        );
        setButtonSizeIndex(clamped);
        editor.site.change({
            mode: 'button-sizing',
            data: { preset: BUTTON_SIZE_PRESETS[clamped].key },
        });
    };

    const [buttonFontScaleIndex, setButtonFontScaleIndex] = useState(() => {
        const raw = editor.site.data?.buttonSizing?.fontScale;
        const parsed = Number.parseFloat(String(raw ?? ''));
        const target = Number.isFinite(parsed) ? parsed : 1;
        let closest = 0;
        let diff = Infinity;
        BUTTON_FONT_SCALE_PRESETS.forEach((preset, i) => {
            const d = Math.abs(preset.scale - target);
            if (d < diff) {
                diff = d;
                closest = i;
            }
        });
        return closest;
    });

    useEffect(() => {
        const raw = editor.site.data?.buttonSizing?.fontScale;
        const parsed = Number.parseFloat(String(raw ?? ''));
        const target = Number.isFinite(parsed) ? parsed : 1;
        let closest = 0;
        let diff = Infinity;
        BUTTON_FONT_SCALE_PRESETS.forEach((preset, i) => {
            const d = Math.abs(preset.scale - target);
            if (d < diff) {
                diff = d;
                closest = i;
            }
        });
        setButtonFontScaleIndex(closest);
    }, [editor.site.data?.buttonSizing?.fontScale]);

    const applyButtonFontScaleIndex = (idx) => {
        const clamped = Math.max(
            0,
            Math.min(BUTTON_FONT_SCALE_PRESETS.length - 1, idx | 0)
        );
        setButtonFontScaleIndex(clamped);
        editor.site.change({
            mode: 'button-font-scale',
            data: { fontScale: BUTTON_FONT_SCALE_PRESETS[clamped].scale },
        });
    };

    const [buttonBoldnessIndex, setButtonBoldnessIndex] = useState(() => {
        const raw = editor.site.data?.buttonSizing?.fontWeight;
        const parsed =
            typeof raw === 'number'
                ? raw
                : Number.parseInt(String(raw ?? ''), 10);
        const target = Number.isFinite(parsed) ? parsed : 700;
        let closest = 0;
        let diff = Infinity;
        BUTTON_BOLDNESS_PRESETS.forEach((preset, i) => {
            const d = Math.abs(preset.weight - target);
            if (d < diff) {
                diff = d;
                closest = i;
            }
        });
        return closest;
    });

    useEffect(() => {
        const raw = editor.site.data?.buttonSizing?.fontWeight;
        const parsed =
            typeof raw === 'number'
                ? raw
                : Number.parseInt(String(raw ?? ''), 10);
        const target = Number.isFinite(parsed) ? parsed : 700;
        let closest = 0;
        let diff = Infinity;
        BUTTON_BOLDNESS_PRESETS.forEach((preset, i) => {
            const d = Math.abs(preset.weight - target);
            if (d < diff) {
                diff = d;
                closest = i;
            }
        });
        setButtonBoldnessIndex(closest);
    }, [editor.site.data?.buttonSizing?.fontWeight]);

    const applyButtonBoldnessIndex = (idx) => {
        const clamped = Math.max(
            0,
            Math.min(BUTTON_BOLDNESS_PRESETS.length - 1, idx | 0)
        );
        setButtonBoldnessIndex(clamped);
        editor.site.change({
            mode: 'button-font-weight',
            data: { fontWeight: BUTTON_BOLDNESS_PRESETS[clamped].weight },
        });
    };

    /* ---- Button gradient + border ---- */
    const applyButtonGradient = ({
        variant,
        state,
        angle,
        colors,
        textColor,
    }) => {
        editor.site.change({
            mode: 'button-gradient',
            data: {
                variant,
                state,
                angle,
                colors,
                textColor,
            },
        });
    };

    const [buttonBorderWidth, setButtonBorderWidth] = useState(() => {
        const raw = editor.site.data?.colors?.button?.['border-width'] || '0px';
        const px = Number.parseInt(String(raw), 10);
        if (!Number.isFinite(px)) return 0;
        return Math.max(0, Math.min(5, px));
    });

    const currentBorderWidth =
        editor.site.data?.colors?.button?.['border-width'];

    useEffect(() => {
        const px = Number.parseInt(String(currentBorderWidth || '0px'), 10);
        if (Number.isFinite(px)) {
            setButtonBorderWidth(Math.max(0, Math.min(5, px)));
        }
    }, [currentBorderWidth]);

    const applyButtonBorder = ({
        variant,
        state,
        width,
        color,
        colors,
        angle,
    }) => {
        editor.site.change({
            mode: 'button-border',
            data: {
                variant,
                state,
                width,
                color,
                colors,
                angle,
            },
        });
    };

    const applyButtonShadow = ({ variant, state, mode }) => {
        editor.site.change({
            mode: 'button-shadow',
            data: {
                variant,
                state,
                mode,
            },
        });
    };

    const applyButtonHoverAnimation = ({ variant, animation }) => {
        editor.site.change({
            mode: 'button-hover-animation',
            data: {
                variant,
                animation,
            },
        });
    };

    const applyButtonIcon = ({ variant, state, icon }) => {
        editor.site.change({
            mode: 'button-icon',
            data: {
                variant,
                state,
                icon,
            },
        });
    };

    const applyButtonIconPosition = ({ variant, position }) => {
        editor.site.change({
            mode: 'button-icon-position',
            data: {
                variant,
                position,
            },
        });
    };

    const applyButtonIconSize = ({ variant, size }) => {
        editor.site.change({
            mode: 'button-icon-size',
            data: {
                variant,
                size,
            },
        });
    };

    /* ---- Shape radius ---- */
    const RADII = [
        { key: 'none', px: 0 },
        { key: 'sm', px: 2 },
        { key: 'base', px: 4 },
        { key: 'md', px: 6 },
        { key: 'lg', px: 8 },
        { key: 'xl', px: 12 },
        { key: '2xl', px: 16 },
        { key: '3xl', px: 24 },
        { key: 'full', px: 9999 },
    ];
    const keyToIndex = new Map(RADII.map((r, i) => [r.key, i]));
    const [radiusIndex, setRadiusIndex] = useState(keyToIndex.get('md') || 3);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const raw =
            getComputedStyle(document.documentElement).getPropertyValue(
                '--shape-radius'
            ) || '6px';
        const px = parseCssLengthToPx(raw);
        if (!Number.isFinite(px)) return;
        let closest = 0,
            diff = Infinity;
        RADII.forEach((r, i) => {
            const d = Math.abs(r.px - px);
            if (d < diff) {
                diff = d;
                closest = i;
            }
        });
        setRadiusIndex(closest);
    }, []);

    const applyRadiusIndex = (idx) => {
        const clamped = Math.max(0, Math.min(RADII.length - 1, idx | 0));
        setRadiusIndex(clamped);
        const px = RADII[clamped].px;
        editor.site.change({
            mode: 'shape-radius',
            data: { value: `${px}px` },
        });
    };

    const changeHeadingFamily = (font) =>
        editor.site.change({ mode: 'font', data: { type: 'display', font } });
    const changeHeadingWeight = (weight) =>
        editor.site.change({
            mode: 'font-weight',
            data: { type: 'display', weight },
        });
    const changeBodyFamily = (font) =>
        editor.site.change({ mode: 'font', data: { type: 'text', font } });
    const changeBodyWeight = (weight) =>
        editor.site.change({
            mode: 'font-weight',
            data: { type: 'text', weight },
        });
    const changeLogoTextFamily = (font) =>
        editor.global.change({
            key: 'logoTextFont',
            value: sanitizeFontFamilyInput(font),
        });
    const changeLogoTextWeight = (weight) =>
        editor.global.change({
            key: 'logoTextFontWeight',
            value: String(weight),
        });
    const addCustomHeadingFont = async (font) => {
        const normalized = sanitizeFontFamilyInput(font);
        if (!normalized) return [400];
        const normalizedLower = normalized.toLowerCase();
        const existsInPreset = DISPLAY_FONTS.some(
            ({ font: preset }) =>
                sanitizeFontFamilyInput(preset).toLowerCase() ===
                normalizedLower
        );
        const resolvedWeights = existsInPreset
            ? normalizeWeightList(weightsForFamily(normalized, DISPLAY_FONTS))
            : await fetchGoogleFontWeights(normalized);
        setCustomFontWeights((prev) => ({
            ...prev,
            [normalizedLower]: resolvedWeights,
        }));
        if (!existsInPreset) {
            setCustomDisplayFonts((prev) =>
                uniqueCustomFonts([normalized, ...prev])
            );
        }
        setHiddenDisplayFonts((prev) =>
            prev.filter(
                (entry) =>
                    sanitizeFontFamilyInput(entry).toLowerCase() !==
                    normalizedLower
            )
        );
        return resolvedWeights;
    };
    const addCustomBodyFont = async (font) => {
        const normalized = sanitizeFontFamilyInput(font);
        if (!normalized) return [400];
        const normalizedLower = normalized.toLowerCase();
        const existsInPreset = TEXT_FONTS.some(
            ({ font: preset }) =>
                sanitizeFontFamilyInput(preset).toLowerCase() ===
                normalizedLower
        );
        const resolvedWeights = existsInPreset
            ? normalizeWeightList(weightsForFamily(normalized, TEXT_FONTS))
            : await fetchGoogleFontWeights(normalized);
        setCustomFontWeights((prev) => ({
            ...prev,
            [normalizedLower]: resolvedWeights,
        }));
        if (!existsInPreset) {
            setCustomTextFonts((prev) =>
                uniqueCustomFonts([normalized, ...prev])
            );
        }
        setHiddenTextFonts((prev) =>
            prev.filter(
                (entry) =>
                    sanitizeFontFamilyInput(entry).toLowerCase() !==
                    normalizedLower
            )
        );
        return resolvedWeights;
    };

    const uploadFont = async (
        file,
        requestedFamily,
        setCustomFonts,
        setHiddenFonts
    ) => {
        if (file.size > 2 * 1024 * 1024) {
            notifications.push({
                title: 'Font file is larger than 2 MB',
                variant: 'error',
                ttl: 10,
            });
            return null;
        }

        const family =
            sanitizeFontFamilyInput(requestedFamily) ||
            inferFontFamilyFromFileName(file.name);
        if (!family) return null;

        try {
            const previewUrl = await readFileAsDataUrl(file);
            const uploaded = await API.post(`${company_id}/fonts`, {
                fileName: file.name,
                family,
                weight: inferFontWeightFromFileName(file.name),
                style: /italic/i.test(file.name) ? 'italic' : 'normal',
                data: previewUrl,
            });
            uploaded.previewUrl = previewUrl;
            const nextUploadedFonts = [
                ...uploadedFonts.filter(
                    (font) =>
                        !(
                            font.family.toLowerCase() ===
                                uploaded.family.toLowerCase() &&
                            String(font.weight) === String(uploaded.weight) &&
                            font.style === uploaded.style
                        )
                ),
                uploaded,
            ];
            editor.global.change({
                key: 'uploadedFonts',
                value: nextUploadedFonts,
            });
            setCustomFonts((prev) =>
                uniqueCustomFonts([uploaded.family, ...prev])
            );
            setHiddenFonts((prev) =>
                prev.filter(
                    (font) =>
                        sanitizeFontFamilyInput(font).toLowerCase() !==
                        uploaded.family.toLowerCase()
                )
            );
            setCustomFontWeights((prev) => ({
                ...prev,
                [uploaded.family.toLowerCase()]: normalizeWeightList([
                    ...(prev[uploaded.family.toLowerCase()] || []),
                    uploaded.weight,
                ]),
            }));
            notifications.push({
                title: `${uploaded.family} uploaded`,
                variant: 'success',
                ttl: 10,
            });
            return uploaded;
        } catch (error) {
            notifications.push({
                title: 'Font upload failed',
                variant: 'error',
                ttl: 10,
                error,
            });
            return null;
        }
    };

    const removeHeadingFont = (font) => {
        const normalized = sanitizeFontFamilyInput(font);
        if (!normalized) return;
        const normalizedLower = normalized.toLowerCase();
        const nextCustomFonts = customDisplayFonts.filter(
            (entry) =>
                sanitizeFontFamilyInput(entry).toLowerCase() !== normalizedLower
        );
        const nextHiddenFonts = uniqueCustomFonts([
            normalized,
            ...hiddenDisplayFonts,
        ]);

        setCustomDisplayFonts(nextCustomFonts);
        setHiddenDisplayFonts(nextHiddenFonts);

        if (
            sanitizeFontFamilyInput(headingFamily).toLowerCase() !==
            normalizedLower
        ) {
            return;
        }

        const nextOptions = buildFontOptions({
            fonts: DISPLAY_FONTS,
            customFonts: nextCustomFonts,
            hiddenFonts: nextHiddenFonts,
            customFontWeights,
        });
        const fallback = nextOptions[0];
        if (!fallback?.font) return;
        changeHeadingFamily(fallback.font);
        const fallbackWeight =
            weightsForFamily(fallback.font, nextOptions)[0] || 400;
        changeHeadingWeight(fallbackWeight);
    };

    const removeBodyFont = (font) => {
        const normalized = sanitizeFontFamilyInput(font);
        if (!normalized) return;
        const normalizedLower = normalized.toLowerCase();
        const nextCustomFonts = customTextFonts.filter(
            (entry) =>
                sanitizeFontFamilyInput(entry).toLowerCase() !== normalizedLower
        );
        const nextHiddenFonts = uniqueCustomFonts([
            normalized,
            ...hiddenTextFonts,
        ]);

        setCustomTextFonts(nextCustomFonts);
        setHiddenTextFonts(nextHiddenFonts);

        if (
            sanitizeFontFamilyInput(bodyFamily).toLowerCase() !==
            normalizedLower
        ) {
            return;
        }

        const nextOptions = buildFontOptions({
            fonts: TEXT_FONTS,
            customFonts: nextCustomFonts,
            hiddenFonts: nextHiddenFonts,
            customFontWeights,
        });
        const fallback = nextOptions[0];
        if (!fallback?.font) return;
        changeBodyFamily(fallback.font);
        const fallbackWeight =
            weightsForFamily(fallback.font, nextOptions)[0] || 400;
        changeBodyWeight(fallbackWeight);
    };

    const removeLogoTextFont = (font) => {
        removeHeadingFont(font);
        if (
            sanitizeFontFamilyInput(logoTextFontValue).toLowerCase() !==
            sanitizeFontFamilyInput(font).toLowerCase()
        ) {
            return;
        }

        editor.global.change({ key: 'logoTextFont', value: '' });
        editor.global.change({ key: 'logoTextFontWeight', value: '' });
    };

    const googleFontExtras = useMemo(
        () =>
            uniqueCustomFonts([
                headingFamily,
                bodyFamily,
                logoTextFontValue,
                ...customDisplayFonts,
                ...customTextFonts,
            ]).map((font) => {
                const fontKey = sanitizeFontFamilyInput(font).toLowerCase();
                const logoFontKey =
                    sanitizeFontFamilyInput(logoTextFontValue).toLowerCase();
                return {
                    font,
                    weights:
                        customFontWeights[fontKey] ||
                        (logoFontKey && fontKey === logoFontKey
                            ? [logoTextFontWeightValue]
                            : [400]),
                };
            }),
        [
            bodyFamily,
            customDisplayFonts,
            customFontWeights,
            customTextFonts,
            headingFamily,
            logoTextFontValue,
            logoTextFontWeightValue,
        ]
    );

    const hasAppliedInitialIconDom = useRef(false);
    const iconOverrideRef = useRef({
        pack: iconPackOverride,
        style: iconStyleOverride,
    });
    const iconObserverRef = useRef(null);
    const componentSnapshotRef = useRef(null);
    const prevDrawerOpenRef = useRef(
        typeof editor.site.visible === 'function' ? editor.site.visible() : true
    );
    const originalCloseRef = useRef(editor.site.close);

    useEffect(() => {
        return () => {
            iconObserverRef.current = null;
        };
    }, []);

    /* ---- icon overrides ---- */
    const applyIconOverride = ({
        showToast = true,
        pack: overridePack,
        style: overrideStyle,
    } = {}) => {
        const pack = overridePack || iconPackOverride || 'classic';
        const style = normalizeSiteIconStyle(
            overrideStyle || iconStyleOverride,
            pack
        );
        iconOverrideRef.current = { pack, style };
        applyIconOverrideDom(pack, style);
        setTimeout(() => applyIconOverrideDom(pack, style), 0);
        applyOverrideToComponents(pack, style);
        if (showToast) {
            notifications.push({
                title: 'Sitewide icon style applied (preview)',
                variant: 'success',
                ttl: 4,
            });
        }
    };

    const handleIconPackOverride = (pack) => {
        overrideSavedRef.current = false;
        setIconPackOverride(pack);
        const nextStyle = normalizeSiteIconStyle(iconStyleOverride, pack);
        setIconStyleOverride(nextStyle);
        iconOverrideRef.current = { pack, style: nextStyle };
        applyIconOverride({ showToast: false, pack, style: nextStyle });
    };

    const handleIconStyleOverride = (style) => {
        overrideSavedRef.current = false;
        const normalized = normalizeSiteIconStyle(style, iconPackOverride);
        setIconStyleOverride(normalized);
        iconOverrideRef.current = { pack: iconPackOverride, style: normalized };
        applyIconOverride({
            showToast: false,
            pack: iconPackOverride,
            style: normalized,
        });
    };

    const handleShapeDividerOverride = (shape) => {
        overrideSavedRef.current = false;
        const normalized = normalizeShapeDividerShape(shape);
        setShapeDividerOverride(normalized);
        applyShapeDividerOverrideToComponents(normalized);
        notifications.push({
            title: 'Sitewide shape divider style applied (preview)',
            variant: 'success',
            ttl: 4,
        });
    };

    const revertPreview = () => {
        setIconPackOverride(savedPackRef.current);
        setIconStyleOverride(savedStyleRef.current);
        applyIconOverrideDom(savedPackRef.current, savedStyleRef.current);
        if (componentSnapshotRef.current) {
            componentSnapshotRef.current.forEach((item) => {
                if (!item.file) return;
                editor.components.change({
                    file: item.file,
                    value: item.props,
                });
            });
            componentSnapshotRef.current = null;
        }
        overrideSavedRef.current = false;
    };

    useEffect(() => {
        const id = setInterval(() => {
            const isOpen =
                typeof editor.site.visible === 'function'
                    ? editor.site.visible()
                    : true;
            const wasOpen = prevDrawerOpenRef.current;
            if (wasOpen && !isOpen && !overrideSavedRef.current) {
                revertPreview();
            }
            if (!wasOpen && isOpen) {
                overrideSavedRef.current = false;
                componentSnapshotRef.current = null;
                const reopenPack = savedPackRef.current;
                const reopenStyle = normalizeSiteIconStyle(
                    savedStyleRef.current,
                    reopenPack
                );
                setIconPackOverride(reopenPack);
                setIconStyleOverride(reopenStyle);
            }
            prevDrawerOpenRef.current = isOpen;
        }, 300);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        originalCloseRef.current = editor.site.close;
        editor.site.close = () => {
            if (!overrideSavedRef.current) {
                revertPreview();
            }
            originalCloseRef.current?.();
        };
        return () => {
            if (originalCloseRef.current) {
                editor.site.close = originalCloseRef.current;
            }
        };
    }, []);

    useEffect(() => {
        return () => {
            if (!overrideSavedRef.current) {
                revertPreview();
            }
        };
    }, []);

    useEffect(() => {
        return () => {
            if (!overrideSavedRef.current) {
                applyIconOverrideDom(
                    savedPackRef.current,
                    savedStyleRef.current
                );
            }
        };
    }, []);

    const handleMediaPreview = (val) => {
        if (!activeMediaField) return;
        editor.global.change({ key: activeMediaField.key, value: val });
    };

    const handleMediaSelect = (val) => {
        if (!activeMediaField) return;
        editor.global.change({ key: activeMediaField.key, value: val });
        setMediaWasApplied(true);
        setActiveMediaField(null);
    };

    const handleCloseMediaPanel = () => {
        if (activeMediaField && !mediaWasApplied) {
            editor.global.change({
                key: activeMediaField.key,
                value: mediaOriginalValue ?? '',
            });
        }
        setActiveMediaField(null);
        setMediaOriginalValue(null);
        setMediaWasApplied(false);
    };

    const handleSiteExport = async () => {
        if (!company_id || site_exporting) return;
        setSiteExporting(true);
        try {
            const baseUrl =
                process.env.NEXT_PUBLIC_LOCAL_REQUESTS === 'true'
                    ? 'http://localhost:8080'
                    : 'https://sites.nicejob.com';
            const endpoint = `${company_id}/export`;
            const useProxy = await API.checkProxyAvailable();
            const response = useProxy
                ? await fetch('/api/editor-proxy', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                          method: 'POST',
                          url: `${baseUrl}/${endpoint}`,
                      }),
                  })
                : await fetch(`${baseUrl}/${endpoint}`, {
                      method: 'POST',
                      credentials: 'include',
                  });
            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(
                    payload?.message || payload?.error || 'Site export failed.'
                );
            }
            const blob = await response.blob();
            const disposition =
                response.headers.get('content-disposition') || '';
            const clientSlug = String(company_name || 'client')
                .normalize('NFKD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');
            const filename =
                disposition.match(/filename="?([^";]+)"?/i)?.[1] ||
                `${company_id}-${clientSlug || 'client'}-site-export.zip`;
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(downloadUrl);
            notifications.push({
                title: 'Site export ready',
                message: 'Your static ZIP has been downloaded.',
                variant: 'success',
                ttl: 10,
            });
        } catch (error) {
            notifications.push({
                title: 'Site export failed',
                message:
                    error instanceof Error
                        ? error.message
                        : 'Please try again after publishing the site.',
                variant: 'error',
                ttl: 20,
                error,
            });
        } finally {
            setSiteExporting(false);
        }
    };

    /* ---- save ---- */
    const handleSave = () => {
        const saveCustom = async () => {
            const normalizedCustomCode = {
                head: normalizeJsonLdScript(custom_code?.head || ''),
                footer: normalizeJsonLdScript(custom_code?.footer || ''),
            };

            if (
                JSON.stringify(saved_custom_code) !==
                JSON.stringify(normalizedCustomCode)
            ) {
                setSavingTags(true);
                await API.post(`${company_id}/custom-code`, {
                    head: normalizedCustomCode?.head
                        ? normalizedCustomCode.head
                        : null,
                    footer: normalizedCustomCode?.footer
                        ? normalizedCustomCode.footer
                        : null,
                });
                setCustomCode(normalizedCustomCode);
                setSavedCustomCode(normalizedCustomCode);
                setSavingTags(false);
            }
        };

        Promise.resolve()
            .then(saveCustom)
            .then(() => {
                // persist icon override into site data and components
                const pack = iconPackOverride || 'classic';
                const style = normalizeSiteIconStyle(iconStyleOverride, pack);
                editor.site.change({
                    mode: 'icon-pack-override',
                    data: { pack, style },
                });
                applyOverrideToComponents(pack, style);
                savedPackRef.current = pack;
                savedStyleRef.current = style;
                overrideSavedRef.current = true;
                componentSnapshotRef.current = null;

                editor.save();
                notifications.push({
                    title: 'Site settings saved',
                    variant: 'success',
                    ttl: 8,
                });
            })
            .catch((error) => {
                notifications.push({
                    title: 'Failed to save site settings',
                    variant: 'error',
                    ttl: 20,
                    error,
                });
            })
            .finally(() => editor.site.close());
    };

    const generateFaviconFromDataUrl = async (imageDataUrl) => {
        setSavingFavicon(true);
        setFaviconSaved(false);
        setFavicon(imageDataUrl);
        try {
            await API.post(`${company_id}/favicon/generate`, {
                company_name: editor.global.data.company_name,
                image: imageDataUrl,
            });
            setFaviconSaved(true);
            setHasCustomFavicon(true);
            window.dispatchEvent(new CustomEvent('editor:favicon-updated'));
            notifications.push({
                title: 'Favicon successfully generated!',
                variant: 'success',
                ttl: 10,
            });
        } catch (error) {
            setFavicon('');
            setHasCustomFavicon(false);
            notifications.push({
                title: 'Failed to generate favicon!',
                variant: 'error',
                ttl: 20,
                error,
            });
        } finally {
            setSavingFavicon(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        fetch('/api/favicon-status')
            .then((response) => {
                if (!response.ok) throw new Error('Unable to audit favicon');
                return response.json();
            })
            .then((status) => {
                if (!cancelled) {
                    setHasCustomFavicon(status?.hasCustomFavicon === true);
                }
            })
            .catch(() => {
                if (!cancelled) setHasCustomFavicon(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const uploadBrandImage = async (image) => {
        const normalizedImage = await normalizeSvgFileForUpload(image);
        const formData = new FormData();
        formData.append('image', normalizedImage);
        const url =
            `https://image-search-ytnjkd5upa-uc.a.run.app/upload/image/${company_id}?` +
            new URLSearchParams({
                filename: normalizedImage.name
                    .replace(/ +/g, '-')
                    .toLowerCase(),
            });

        const response = await fetch(url, {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Image upload failed: ${response.statusText}`);
        }

        const data = await response.json();
        editor.site.addImage?.({
            ...data,
            size_bytes: normalizedImage.size,
        });
        return data.url;
    };

    /* ---- favicon ---- */
    const handleFaviconUpload = async (event) => {
        const image = event.target.files[0];
        if (!image) {
            setSavingFavicon(false);
            return;
        }
        event.target.value = '';
        const reader = new FileReader();
        reader.readAsDataURL(image);
        reader.onloadend = async () => {
            await generateFaviconFromDataUrl(String(reader.result || ''));
        };
        reader.onerror = () => {
            notifications.push({
                title: 'Failed to read image for favicon generation!',
                variant: 'error',
                ttl: 20,
            });
            setSavingFavicon(false);
        };
    };

    const handleLogoUpload = async (event) => {
        const image = event.target.files?.[0];
        if (!image) return;
        event.target.value = '';

        setSavingLogo(true);
        try {
            const url = await uploadBrandImage(image);
            editor.global.change({ key: 'logoUrl', value: url });
            setLogoAssetPickerOpen(true);
        } catch (error) {
            notifications.push({
                title: 'Image upload failed',
                variant: 'error',
                ttl: 20,
                error,
            });
        } finally {
            setSavingLogo(false);
        }
    };

    const handleGenerateLlmsText = async () => {
        if (llms_generating) return;
        setLlmsGenerating(true);
        try {
            const response = await fetch('/api/llms-txt/generate');
            if (!response.ok) {
                throw new Error('Failed to generate llms.txt');
            }
            const data = await response.json();
            if (!data?.text) {
                throw new Error('No llms.txt content returned');
            }
            setLlmsEditorValue(data.text);
            setLlmsUsesGeneratedDefault(false);
            editor.global.change({ key: 'llms_txt', value: data.text });
            notifications.push({
                title: 'LLMs.txt generated',
                variant: 'success',
                ttl: 10,
            });
        } catch (error) {
            notifications.push({
                title: 'Failed to generate LLMs.txt',
                variant: 'error',
                ttl: 20,
                error,
            });
        } finally {
            setLlmsGenerating(false);
        }
    };

    const handleOpenSeoRecommendations = () => {
        const ownerWindow =
            settingsScrollRef.current?.ownerDocument?.defaultView || window;
        ownerWindow.dispatchEvent(
            new ownerWindow.CustomEvent('nicejob:open-alt-text', {
                detail: { focus: 'alt-text' },
            })
        );
    };

    const iconPackDropdownOptions = SITE_ICON_PACKS.map((pack) => ({
        value: pack.key,
        label: pack.label,
    }));
    const iconStyleDropdownOptions = (
        SITE_PACK_STYLE_OPTIONS[iconPackOverride] || []
    ).map((style) => ({
        value: style.key,
        label: style.label,
    }));
    const shapeDividerCount = shapeDividerComponents.length;
    const shapeDividerOverrideStatusText =
        shapeDividerCount > 0
            ? `${shapeDividerCount} shape divider${
                  shapeDividerCount === 1 ? '' : 's'
              } will update across the site. Preview only until you click Save.`
            : 'No shape dividers found.';
    const logoAltText = String(editor.global.data?.logoAltText || '').trim();
    const displayLogoAltText =
        logoAltText || editor.global.data?.company_name || '';
    const faviconPreviewSrc =
        favicon ||
        (hasCustomFavicon
            ? `/icons/apple-touch-icon.png?v=${editorVersion}`
            : '');
    const logoPreviewSrc = String(editor.global.data?.logoUrl || '').trim();

    return (
        <>
            <GoogleFontLoader
                extraFonts={googleFontExtras}
                uploadedFonts={uploadedFonts}
            />
            <EditorDrawer
                storageKey="globalEditorDrawerv2"
                bodyRef={settingsScrollRef}
                title={
                    activeMediaField ? (
                        <button
                            onClick={handleCloseMediaPanel}
                            className="flex items-center gap-2 text-white transition-colors hover:text-white/80"
                        >
                            <ArrowLeftIcon className="h-5 w-5" />
                            <span>Back to Site Settings</span>
                        </button>
                    ) : (
                        'Site Settings'
                    )
                }
                open={editor.site.visible()}
                onClose={
                    activeMediaField ? handleCloseMediaPanel : editor.site.close
                }
                onCancel={() => {
                    if (activeMediaField) {
                        handleCloseMediaPanel();
                        return;
                    }
                    editor.site.cancel();
                    setFavicon(null);
                    setSavingFavicon(false);
                    setFaviconSaved(false);
                    setLogoAssetPickerOpen(false);
                    setIsEditingLogoAlt(false);
                    setLogoAltDraft('');
                    setCustomCode(saved_custom_code);
                    if (typeof window !== 'undefined') {
                        const root = document.documentElement;
                        const p =
                            root.style.getPropertyValue('--primary-700') ||
                            getComputedStyle(root).getPropertyValue(
                                '--primary-700'
                            );
                        const s =
                            root.style.getPropertyValue('--secondary-700') ||
                            getComputedStyle(root).getPropertyValue(
                                '--secondary-700'
                            );
                        if (p?.trim())
                            setPrimaryHex(normalizeHex(p.trim(), '#8F52F1'));
                        if (s?.trim())
                            setSecondaryHex(normalizeHex(s.trim(), '#06B6D4'));
                    }
                    editor.site.close();
                }}
                onSave={handleSave}
                hideFooter={!!activeMediaField}
            >
                {activeMediaField ? (
                    <div className="relative flex h-full flex-col">
                        <div className="relative flex-1">
                            <MediaPanel
                                currentValue={activeMediaField.value}
                                onSelectImage={handleMediaSelect}
                                onSelectVideo={handleMediaSelect}
                                onSelectCustom={handleMediaSelect}
                                onPreviewSelect={handleMediaPreview}
                            />
                        </div>
                    </div>
                ) : (
                    <>
                        {/* ===== Brand ===== */}
                        <section className="mt-3">
                            <div className="text-md mb-3 flex items-center gap-2 font-bold text-primary-700">
                                <StarIcon
                                    className="h-5 w-5 text-primary-700"
                                    aria-hidden="true"
                                />
                                <span>Brand</span>
                            </div>

                            <div className="flex flex-col gap-4">
                                <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
                                    <div className="flex items-center gap-2 px-3 py-2">
                                        <div className="flex min-w-0 flex-1 items-center gap-3 rounded-lg p-1 text-left">
                                            {faviconPreviewSrc ? (
                                                <img
                                                    src={faviconPreviewSrc}
                                                    alt=""
                                                    className="h-10 w-10 shrink-0 rounded-full ring-1 ring-black/10"
                                                />
                                            ) : (
                                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-[10px] font-semibold text-neutral-400 ring-1 ring-black/10">
                                                    None
                                                </div>
                                            )}
                                            <div className="min-w-0">
                                                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-500">
                                                    Favicon
                                                </div>
                                                <div className="mt-1 truncate text-sm font-semibold text-neutral-900">
                                                    {favicon || hasCustomFavicon
                                                        ? 'Custom favicon selected'
                                                        : 'Upload a Fav Icon Image'}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-1">
                                            {saving_favicon ? (
                                                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary-50 text-primary-700">
                                                    <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                                                </span>
                                            ) : favicon_saved ? (
                                                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-green-50 text-green-600">
                                                    <CheckCircleIcon className="h-3.5 w-3.5" />
                                                </span>
                                            ) : null}

                                            <label
                                                htmlFor="favicon_input"
                                                title="Upload a file"
                                                onClick={(event) =>
                                                    event.stopPropagation()
                                                }
                                                className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-full text-primary-700 transition-all hover:bg-primary-50"
                                            >
                                                <ArrowUpTrayIcon className="h-3.5 w-3.5" />
                                                <input
                                                    id="favicon_input"
                                                    type="file"
                                                    accept="image/svg+xml, image/png, image/jpeg"
                                                    className="sr-only"
                                                    onChange={
                                                        handleFaviconUpload
                                                    }
                                                    disabled={saving_favicon}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
                                    <div
                                        className={`flex items-center gap-2 px-3 py-2 ${
                                            logoAssetPickerOpen
                                                ? 'border-b border-neutral-200'
                                                : ''
                                        }`}
                                    >
                                        <div className="flex min-w-0 flex-1 items-center gap-3">
                                            {logoPreviewSrc ? (
                                                <div className="flex h-10 shrink-0 items-center">
                                                    <img
                                                        src={logoPreviewSrc}
                                                        alt=""
                                                        className="h-10 w-auto max-w-32 object-contain object-left"
                                                    />
                                                </div>
                                            ) : null}
                                            {isEditingLogoAlt ? (
                                                <form
                                                    onSubmit={(event) => {
                                                        event.preventDefault();
                                                        editor.global.change({
                                                            key: 'logoAltText',
                                                            value: logoAltDraft.trim(),
                                                        });
                                                        setIsEditingLogoAlt(
                                                            false
                                                        );
                                                    }}
                                                    className="flex min-w-0 flex-1 items-center gap-1.5"
                                                >
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-500">
                                                            Logo
                                                        </div>
                                                        <div className="mt-1 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                            <input
                                                                type="text"
                                                                value={
                                                                    logoAltDraft
                                                                }
                                                                onChange={(
                                                                    event
                                                                ) =>
                                                                    setLogoAltDraft(
                                                                        event
                                                                            .target
                                                                            .value
                                                                    )
                                                                }
                                                                placeholder="Logo alt text"
                                                                className="block h-6 w-full rounded-[10px] border-0 bg-white px-2 text-[11px] leading-4 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                                                autoFocus
                                                            />
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="submit"
                                                        title="Save logo alt text"
                                                        className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-white transition-all hover:bg-primary-500"
                                                    >
                                                        <CheckIcon className="h-3 w-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title="Cancel logo alt text edit"
                                                        onClick={() => {
                                                            setIsEditingLogoAlt(
                                                                false
                                                            );
                                                            setLogoAltDraft(
                                                                displayLogoAltText
                                                            );
                                                        }}
                                                        className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-neutral-300 text-neutral-600 transition-all hover:bg-neutral-100"
                                                    >
                                                        <XMarkIcon className="h-3 w-3" />
                                                    </button>
                                                </form>
                                            ) : (
                                                <div
                                                    role="button"
                                                    tabIndex={0}
                                                    className="focus-visible:ring-primary-500/60 group min-w-0 flex-1 rounded-lg p-1 text-left transition-colors focus-visible:outline-none focus-visible:ring-2"
                                                    aria-expanded={
                                                        logoAssetPickerOpen
                                                    }
                                                    onClick={() => {
                                                        setLogoAssetPickerOpen(
                                                            (open) => !open
                                                        );
                                                    }}
                                                    onKeyDown={(event) => {
                                                        if (
                                                            event.key ===
                                                                'Enter' ||
                                                            event.key === ' '
                                                        ) {
                                                            event.preventDefault();
                                                            setLogoAssetPickerOpen(
                                                                (open) => !open
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-500">
                                                        Logo
                                                    </div>
                                                    <div className="mt-1 flex min-w-0 items-center gap-1">
                                                        <div className="min-w-0 truncate text-sm font-semibold text-neutral-900">
                                                            {displayLogoAltText ||
                                                                'Uses company name by default'}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            title="Edit logo alt text"
                                                            onClick={(
                                                                event
                                                            ) => {
                                                                event.stopPropagation();
                                                                setLogoAltDraft(
                                                                    displayLogoAltText
                                                                );
                                                                setIsEditingLogoAlt(
                                                                    true
                                                                );
                                                            }}
                                                            className={
                                                                INLINE_EDIT_BUTTON_CLASSNAME
                                                            }
                                                        >
                                                            <PencilSquareIcon className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-1">
                                            {saving_logo ? (
                                                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary-50 text-primary-700">
                                                    <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                                                </span>
                                            ) : null}

                                            <label
                                                htmlFor="logo_input"
                                                title="Upload a file"
                                                onClick={(event) =>
                                                    event.stopPropagation()
                                                }
                                                className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-full text-primary-700 transition-all hover:bg-primary-50"
                                            >
                                                <ArrowUpTrayIcon className="h-3.5 w-3.5" />
                                                <input
                                                    id="logo_input"
                                                    type="file"
                                                    accept="image/*"
                                                    className="sr-only"
                                                    onChange={handleLogoUpload}
                                                    disabled={saving_logo}
                                                />
                                            </label>

                                            <button
                                                type="button"
                                                aria-expanded={
                                                    logoAssetPickerOpen
                                                }
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setLogoAssetPickerOpen(
                                                        (open) => !open
                                                    );
                                                }}
                                                className="inline-flex h-6 w-6 items-center justify-center rounded-full text-primary-700 transition-all hover:bg-primary-50"
                                            >
                                                <ChevronDownIcon
                                                    className={`h-3 w-3 transition-transform ${
                                                        logoAssetPickerOpen
                                                            ? 'rotate-180'
                                                            : ''
                                                    }`}
                                                />
                                            </button>
                                        </div>
                                    </div>

                                    {logoAssetPickerOpen ? (
                                        <div className="bg-white px-4 py-3">
                                            <MediaPanel
                                                inline
                                                hidePreview
                                                currentValue={
                                                    editor.global.data?.logoUrl
                                                }
                                                onSelectImage={(val) => {
                                                    editor.global.change({
                                                        key: 'logoUrl',
                                                        value: val,
                                                    });
                                                }}
                                                onSelectVideo={(val) => {
                                                    editor.global.change({
                                                        key: 'logoUrl',
                                                        value: val,
                                                    });
                                                }}
                                                onSelectCustom={(val) => {
                                                    editor.global.change({
                                                        key: 'logoUrl',
                                                        value: val,
                                                    });
                                                }}
                                            />
                                            <div className="mt-4 border-t border-neutral-200 pt-3">
                                                <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-500">
                                                    Logo Text
                                                </label>
                                                <div className="mt-2">
                                                    <MDEditor
                                                        value={logoTextValue}
                                                        fieldKey="LogoText"
                                                        componentName="Nav"
                                                        hideLinkButton
                                                        disableLinking
                                                        deferExternalSyncWhileFocused
                                                        onChange={(value) =>
                                                            editor.global.change(
                                                                {
                                                                    key: 'logoText',
                                                                    value,
                                                                }
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <p className="mt-2 text-[11px] leading-4 text-neutral-500">
                                                    Optional markdown logo. Use
                                                    line breaks to control where
                                                    the logo text wraps.
                                                </p>
                                                <div className="mt-3 grid grid-cols-1 gap-3">
                                                    <FontPanel
                                                        type="logo"
                                                        label="Logo Text Font"
                                                        fonts={DISPLAY_FONTS}
                                                        customFonts={
                                                            customDisplayFonts
                                                        }
                                                        hiddenFonts={
                                                            hiddenDisplayFonts
                                                        }
                                                        customFontWeights={
                                                            customFontWeights
                                                        }
                                                        family={
                                                            logoTextFontValue ||
                                                            headingFamily
                                                        }
                                                        weight={
                                                            logoTextFontWeightValue
                                                        }
                                                        onChangeFamily={
                                                            changeLogoTextFamily
                                                        }
                                                        onChangeWeight={
                                                            changeLogoTextWeight
                                                        }
                                                        onAddCustomFont={
                                                            addCustomHeadingFont
                                                        }
                                                        onUploadFont={(
                                                            file,
                                                            family
                                                        ) =>
                                                            uploadFont(
                                                                file,
                                                                family,
                                                                setCustomDisplayFonts,
                                                                setHiddenDisplayFonts
                                                            )
                                                        }
                                                        onRemoveFont={
                                                            removeLogoTextFont
                                                        }
                                                        open={
                                                            fontPanelOpen.logo
                                                        }
                                                        onToggle={() =>
                                                            setFontPanelOpen(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    logo: !prev.logo,
                                                                })
                                                            )
                                                        }
                                                        actions={
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    editor.global.change(
                                                                        {
                                                                            key: 'logoTextFont',
                                                                            value: '',
                                                                        }
                                                                    );
                                                                    editor.global.change(
                                                                        {
                                                                            key: 'logoTextFontWeight',
                                                                            value: '',
                                                                        }
                                                                    );
                                                                }}
                                                                className="inline-flex h-7 items-center rounded-md bg-neutral-100 px-2 text-[11px] font-semibold text-neutral-600 transition hover:bg-neutral-200"
                                                            >
                                                                Use default font
                                                            </button>
                                                        }
                                                    />

                                                    <div className="grid grid-cols-1 gap-3">
                                                        <div>
                                                            <div className="mb-1.5 flex items-center justify-between gap-3">
                                                                <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-500">
                                                                    Text Size
                                                                </label>
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        editor.global.change(
                                                                            {
                                                                                key: 'logoTextSize',
                                                                                value: '',
                                                                            }
                                                                        )
                                                                    }
                                                                    className="text-[11px] font-semibold text-neutral-500 transition hover:text-primary-700"
                                                                >
                                                                    Default
                                                                </button>
                                                            </div>
                                                            <div className="rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                                                <div className="flex h-9 items-center gap-3 rounded-[10px] bg-white px-3">
                                                                    <input
                                                                        type="range"
                                                                        min={
                                                                            LOGO_TEXT_SIZE_MIN
                                                                        }
                                                                        max={
                                                                            LOGO_TEXT_SIZE_MAX
                                                                        }
                                                                        step="1"
                                                                        value={
                                                                            logoTextSizeNumber
                                                                        }
                                                                        onChange={(
                                                                            event
                                                                        ) =>
                                                                            editor.global.change(
                                                                                {
                                                                                    key: 'logoTextSize',
                                                                                    value: `${event.target.value}px`,
                                                                                }
                                                                            )
                                                                        }
                                                                        className="tw-slider flex-1 cursor-pointer"
                                                                        aria-label="Logo text size"
                                                                    />
                                                                    <span className="w-12 text-right text-xs font-semibold text-neutral-700">
                                                                        {
                                                                            logoTextSizeNumber
                                                                        }
                                                                        px
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </section>

                        {/* ===== Theme ===== */}
                        <section className="mt-5">
                            <div className="text-md mb-3 flex items-center gap-2 font-bold text-primary-700">
                                <SwatchIcon
                                    className="h-5 w-5 text-primary-700"
                                    aria-hidden="true"
                                />
                                <span>Theme</span>
                            </div>

                            <div className="mb-2 text-sm font-semibold leading-5 text-neutral-800">
                                Fonts
                            </div>
                            <div className="sm:grid-col2 mt-4 grid grid-cols-1 gap-3">
                                <FontPanel
                                    type="display"
                                    fonts={DISPLAY_FONTS}
                                    customFonts={customDisplayFonts}
                                    hiddenFonts={hiddenDisplayFonts}
                                    customFontWeights={customFontWeights}
                                    family={headingFamily}
                                    weight={headingWeight}
                                    onChangeFamily={changeHeadingFamily}
                                    onChangeWeight={changeHeadingWeight}
                                    onAddCustomFont={addCustomHeadingFont}
                                    onUploadFont={(file, family) =>
                                        uploadFont(
                                            file,
                                            family,
                                            setCustomDisplayFonts,
                                            setHiddenDisplayFonts
                                        )
                                    }
                                    onRemoveFont={removeHeadingFont}
                                    open={fontPanelOpen.display}
                                    onToggle={() =>
                                        setFontPanelOpen((prev) => ({
                                            ...prev,
                                            display: !prev.display,
                                        }))
                                    }
                                />
                                <FontPanel
                                    type="text"
                                    fonts={TEXT_FONTS}
                                    customFonts={customTextFonts}
                                    hiddenFonts={hiddenTextFonts}
                                    customFontWeights={customFontWeights}
                                    family={bodyFamily}
                                    weight={bodyWeight}
                                    onChangeFamily={changeBodyFamily}
                                    onChangeWeight={changeBodyWeight}
                                    onAddCustomFont={addCustomBodyFont}
                                    onUploadFont={(file, family) =>
                                        uploadFont(
                                            file,
                                            family,
                                            setCustomTextFonts,
                                            setHiddenTextFonts
                                        )
                                    }
                                    onRemoveFont={removeBodyFont}
                                    open={fontPanelOpen.text}
                                    onToggle={() =>
                                        setFontPanelOpen((prev) => ({
                                            ...prev,
                                            text: !prev.text,
                                        }))
                                    }
                                />
                            </div>

                            <div className="mb-2 mt-5 text-sm font-semibold leading-5 text-neutral-800">
                                Colors
                            </div>
                            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <ThemeColorField
                                    label="Primary Color"
                                    appliedHex={primaryHex}
                                    defaultColor="#8F52F1"
                                    onApply={applyPrimary}
                                    busy={!!applyingPrimary}
                                    below={
                                        <PaletteStrip
                                            variant="primary"
                                            refreshKey={paletteVersion}
                                        />
                                    }
                                />
                                <ThemeColorField
                                    label="Secondary Color"
                                    appliedHex={secondaryHex}
                                    defaultColor="#06B6D4"
                                    onApply={applySecondary}
                                    busy={!!applyingSecondary}
                                    below={
                                        <PaletteStrip
                                            variant="secondary"
                                            refreshKey={paletteVersion}
                                        />
                                    }
                                />
                            </div>

                            <div className="mt-5 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
                                <div className="mb-2 text-sm font-semibold leading-5 text-neutral-800">
                                    Button Theme
                                </div>
                                <p className="text-[11px] leading-4 text-neutral-600">
                                    Adjust button colors, borders, sizing,
                                    typography, and shape across the site.
                                </p>

                                <div className="mt-4 grid grid-cols-1 gap-3">
                                    <ButtonPanel
                                        variant="primary"
                                        onChange={applyButton}
                                        onGradientApply={applyButtonGradient}
                                        onBorderApply={applyButtonBorder}
                                        onIconChange={applyButtonIcon}
                                        onIconPositionChange={
                                            applyButtonIconPosition
                                        }
                                        onIconSizeChange={applyButtonIconSize}
                                        onShadowChange={applyButtonShadow}
                                        onHoverAnimationChange={
                                            applyButtonHoverAnimation
                                        }
                                        borderWidth={buttonBorderWidth}
                                        siteColors={paletteHexMap}
                                        buttonColors={
                                            editor.site.data?.colors?.button ||
                                            {}
                                        }
                                        open={buttonPanelOpen.primary}
                                        onToggle={() =>
                                            setButtonPanelOpen((prev) => ({
                                                ...prev,
                                                primary: !prev.primary,
                                            }))
                                        }
                                    />
                                    <ButtonPanel
                                        variant="secondary"
                                        onChange={applyButton}
                                        onGradientApply={applyButtonGradient}
                                        onBorderApply={applyButtonBorder}
                                        onIconChange={applyButtonIcon}
                                        onIconPositionChange={
                                            applyButtonIconPosition
                                        }
                                        onIconSizeChange={applyButtonIconSize}
                                        onShadowChange={applyButtonShadow}
                                        onHoverAnimationChange={
                                            applyButtonHoverAnimation
                                        }
                                        borderWidth={buttonBorderWidth}
                                        siteColors={paletteHexMap}
                                        buttonColors={
                                            editor.site.data?.colors?.button ||
                                            {}
                                        }
                                        open={buttonPanelOpen.secondary}
                                        onToggle={() =>
                                            setButtonPanelOpen((prev) => ({
                                                ...prev,
                                                secondary: !prev.secondary,
                                            }))
                                        }
                                    />
                                </div>

                                <div className="mt-4">
                                    <div className="mb-1 text-[11px] font-medium leading-5 text-neutral-500">
                                        Border Width
                                    </div>
                                    <div className="rounded-xl bg-neutral-300 p-[2px]">
                                        <div className="rounded-[10px] bg-white px-3 py-2">
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="range"
                                                    min={0}
                                                    max={5}
                                                    step={1}
                                                    value={buttonBorderWidth}
                                                    onChange={(e) => {
                                                        const next = Number(
                                                            e.target.value
                                                        );
                                                        setButtonBorderWidth(
                                                            next
                                                        );
                                                        applyButtonBorder({
                                                            width: next,
                                                        });
                                                    }}
                                                    className="tw-slider flex-1 cursor-pointer"
                                                />
                                                <span className="w-12 text-right text-xs font-semibold text-neutral-700">
                                                    {buttonBorderWidth}
                                                    px
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <div className="mb-1 text-[11px] font-medium leading-5 text-neutral-400">
                                        Button Size
                                    </div>
                                    <div className="rounded-xl bg-neutral-300 p-[2px]">
                                        <div className="rounded-[10px] bg-white px-3 py-2">
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="range"
                                                    min={0}
                                                    max={
                                                        BUTTON_SIZE_PRESETS.length -
                                                        1
                                                    }
                                                    step={1}
                                                    value={buttonSizeIndex}
                                                    onChange={(e) =>
                                                        applyButtonSizeIndex(
                                                            Number(
                                                                e.target.value
                                                            )
                                                        )
                                                    }
                                                    className="tw-slider flex-1 cursor-pointer"
                                                />
                                                <span className="w-20 text-right text-xs font-semibold text-neutral-700">
                                                    {
                                                        BUTTON_SIZE_PRESETS[
                                                            buttonSizeIndex
                                                        ]?.label
                                                    }
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-3 grid grid-cols-1 gap-3">
                                    <div>
                                        <div className="mb-1 text-[11px] font-medium leading-5 text-neutral-400">
                                            Button Font Size
                                        </div>
                                        <div className="rounded-xl bg-neutral-300 p-[2px]">
                                            <div className="rounded-[10px] bg-white px-3 py-2">
                                                <div className="flex items-center gap-3">
                                                    <input
                                                        type="range"
                                                        min={0}
                                                        max={
                                                            BUTTON_FONT_SCALE_PRESETS.length -
                                                            1
                                                        }
                                                        step={1}
                                                        value={
                                                            buttonFontScaleIndex
                                                        }
                                                        onChange={(e) => {
                                                            applyButtonFontScaleIndex(
                                                                Number(
                                                                    e.target
                                                                        .value
                                                                )
                                                            );
                                                        }}
                                                        className="tw-slider h-6 flex-1 cursor-pointer"
                                                    />
                                                    <span className="w-20 text-right text-xs font-semibold text-neutral-700">
                                                        {
                                                            BUTTON_FONT_SCALE_PRESETS[
                                                                buttonFontScaleIndex
                                                            ]?.label
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="mb-1 text-[11px] font-medium leading-5 text-neutral-400">
                                            Button Font Boldness
                                        </div>
                                        <div className="rounded-xl bg-neutral-300 p-[2px]">
                                            <div className="rounded-[10px] bg-white px-3 py-2">
                                                <div className="flex items-center gap-3">
                                                    <input
                                                        type="range"
                                                        min={0}
                                                        max={
                                                            BUTTON_BOLDNESS_PRESETS.length -
                                                            1
                                                        }
                                                        step={1}
                                                        value={
                                                            buttonBoldnessIndex
                                                        }
                                                        onChange={(e) => {
                                                            applyButtonBoldnessIndex(
                                                                Number(
                                                                    e.target
                                                                        .value
                                                                )
                                                            );
                                                        }}
                                                        className="tw-slider h-6 flex-1 cursor-pointer"
                                                    />
                                                    <span className="w-20 text-right text-xs font-semibold text-neutral-700">
                                                        {
                                                            BUTTON_BOLDNESS_PRESETS[
                                                                buttonBoldnessIndex
                                                            ]?.label
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <div className="mb-1 text-[11px] font-medium leading-5 text-neutral-400">
                                        Shape Radius
                                    </div>
                                    <div className="rounded-xl bg-neutral-300 p-[2px]">
                                        <div className="rounded-[10px] bg-white px-3 py-2">
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="range"
                                                    min={0}
                                                    max={RADII.length - 1}
                                                    step={1}
                                                    value={radiusIndex}
                                                    onChange={(e) =>
                                                        applyRadiusIndex(
                                                            Number(
                                                                e.target.value
                                                            )
                                                        )
                                                    }
                                                    className="tw-slider flex-1 cursor-pointer"
                                                />
                                                <span className="w-10 text-right text-xs font-semibold text-neutral-700">
                                                    {RADII[radiusIndex].key}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
                                <div className="flex flex-col gap-1">
                                    <div className="text-sm font-semibold leading-5 text-neutral-800">
                                        Icon Overrides
                                    </div>
                                    <p className="text-[11px] leading-4 text-neutral-600">
                                        Apply a default icon pack and style
                                        across the site. Existing animations
                                        stay unchanged.
                                    </p>
                                </div>

                                <div className="mt-3 flex flex-wrap gap-3">
                                    <div className="min-w-[180px] rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                        <SitePanelDropdown
                                            value={iconPackOverride}
                                            onChange={handleIconPackOverride}
                                            options={iconPackDropdownOptions}
                                            leading={
                                                <>
                                                    <i
                                                        className="fa-brands fa-font-awesome text-[13px] text-neutral-500"
                                                        aria-hidden="true"
                                                    />
                                                    <span className="text-xs font-semibold text-neutral-700">
                                                        Pack
                                                    </span>
                                                </>
                                            }
                                            buttonClassName="relative flex h-[38px] w-full cursor-default items-center rounded-[10px] bg-white px-3 pr-8 text-left text-sm leading-6 text-neutral-800 focus:outline-none"
                                        />
                                    </div>

                                    <div className="min-w-[180px] rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                        <SitePanelDropdown
                                            value={iconStyleOverride}
                                            onChange={handleIconStyleOverride}
                                            options={iconStyleDropdownOptions}
                                            disabled={
                                                iconStyleDropdownOptions.length <=
                                                1
                                            }
                                            leading={
                                                <>
                                                    <i
                                                        className="fa-solid fa-palette text-[13px] text-neutral-500"
                                                        aria-hidden="true"
                                                    />
                                                    <span className="text-xs font-semibold text-neutral-700">
                                                        Style
                                                    </span>
                                                </>
                                            }
                                            buttonClassName="relative flex h-[38px] w-full cursor-default items-center rounded-[10px] bg-white px-3 pr-8 text-left text-sm leading-6 text-neutral-800 focus:outline-none"
                                        />
                                    </div>
                                </div>

                                <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] leading-4 text-neutral-700">
                                    <i
                                        className="fa-solid fa-eye text-[13px] text-primary-700"
                                        aria-hidden="true"
                                    />
                                    <span>
                                        Preview only until you click Save.
                                    </span>
                                </div>
                            </div>

                            <div className="mt-4 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
                                <div className="flex flex-col gap-1">
                                    <div className="text-sm font-semibold leading-5 text-neutral-800">
                                        Shape Divider Overrides
                                    </div>
                                    <p className="text-[11px] leading-4 text-neutral-600">
                                        Apply one divider shape across the site.
                                        Flip settings and colors stay unchanged.
                                    </p>
                                </div>

                                <div className="mt-3 flex flex-wrap gap-3">
                                    <div className="min-w-[220px] rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                        <SitePanelDropdown
                                            value={shapeDividerOverride}
                                            onChange={
                                                handleShapeDividerOverride
                                            }
                                            options={
                                                SHAPE_DIVIDER_SHAPE_OPTIONS
                                            }
                                            disabled={shapeDividerCount === 0}
                                            leading={
                                                <>
                                                    <span
                                                        className="flex h-4 w-8 items-center justify-center overflow-hidden rounded bg-neutral-100 ring-1 ring-neutral-200"
                                                        aria-hidden="true"
                                                    >
                                                        <svg
                                                            viewBox={
                                                                svgShapes[
                                                                    shapeDividerOverride
                                                                ]?.viewbox ||
                                                                svgShapes.tilt
                                                                    .viewbox
                                                            }
                                                            className="h-full w-full text-neutral-500"
                                                            preserveAspectRatio="none"
                                                        >
                                                            <path
                                                                d={
                                                                    svgShapes[
                                                                        shapeDividerOverride
                                                                    ]?.path ||
                                                                    svgShapes
                                                                        .tilt
                                                                        .path
                                                                }
                                                                fill="currentColor"
                                                            />
                                                        </svg>
                                                    </span>
                                                    <span className="text-xs font-semibold text-neutral-700">
                                                        Shape
                                                    </span>
                                                </>
                                            }
                                            buttonClassName="relative flex h-[38px] w-full cursor-default items-center rounded-[10px] bg-white px-3 pr-8 text-left text-sm leading-6 text-neutral-800 focus:outline-none"
                                        />
                                    </div>
                                </div>

                                <div className="mt-3 text-[11px] leading-4 text-neutral-700">
                                    {shapeDividerOverrideStatusText}
                                </div>
                            </div>
                        </section>

                        {/* ===== SEO ===== */}
                        <section className="mt-5">
                            <div className="text-md mb-3 flex items-center gap-2 font-bold text-primary-700">
                                <GlobeAltIcon
                                    className="h-5 w-5 text-primary-700"
                                    aria-hidden="true"
                                />
                                <span>SEO</span>
                            </div>

                            <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <label className="mb-1 block text-[11px] font-medium leading-5 text-neutral-400">
                                            SEO Recommendations
                                        </label>
                                        <p className="text-[11px] leading-4 text-neutral-500">
                                            Review image alt text, image sizes,
                                            and other SEO optimization issues.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleOpenSeoRecommendations}
                                        className="inline-flex shrink-0 items-center gap-1 rounded-md bg-primary-700 px-2.5 py-1.5 text-[10px] font-semibold text-white shadow-sm transition-colors duration-200 ease-in-out hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                                    >
                                        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                        <span>Open</span>
                                    </button>
                                </div>
                            </div>

                            <div className="mt-4 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <label className="mb-1 block text-[11px] font-medium leading-5 text-neutral-400">
                                            CTA Popup Links
                                        </label>
                                        <p className="text-[11px] leading-4 text-neutral-500">
                                            Open nav CTA custom links in an
                                            iframe popup instead of a new tab.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={embedCtaLink}
                                        onClick={() =>
                                            editor.global.change({
                                                key: 'embed_cta_link',
                                                value: !embedCtaLink,
                                            })
                                        }
                                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ${
                                            embedCtaLink
                                                ? 'bg-primary-700'
                                                : 'bg-neutral-300'
                                        }`}
                                    >
                                        <span
                                            className={`inline-block h-5 w-5 translate-y-0.5 rounded-full bg-white transition-transform duration-200 ${
                                                embedCtaLink
                                                    ? 'translate-x-[1.3rem]'
                                                    : 'translate-x-0.5'
                                            }`}
                                        />
                                    </button>
                                </div>
                            </div>

                            <div className="mt-4 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
                                <label className="mb-1 block text-[11px] font-medium leading-5 text-neutral-400">
                                    Google Tag ID
                                </label>

                                <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                    <div className="flex h-[38px] items-stretch overflow-hidden rounded-[10px] bg-white">
                                        <input
                                            type="text"
                                            spellCheck={false}
                                            placeholder="e.g. G-XXXXXXXXXX or GTM-XXXXXXX"
                                            className="flex-1 border-0 bg-transparent px-2 text-sm leading-6 text-neutral-800 placeholder:text-neutral-400 focus:outline-none"
                                            value={
                                                editor.global.data
                                                    ?.google_tag || ''
                                            }
                                            onChange={(e) =>
                                                editor.global.change({
                                                    key: 'google_tag',
                                                    value: e.target.value.trim(),
                                                })
                                            }
                                        />
                                    </div>
                                </div>

                                <p className="mt-2 text-[11px] leading-4 text-neutral-500">
                                    Paste your Google Analytics, Ads, or Tag
                                    Manager ID. The tracking script will be
                                    automatically injected into the site’s
                                    &lt;head&gt;.
                                </p>
                            </div>
                            <div className="mt-4 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
                                <label className="mb-1 block text-[11px] font-medium leading-5 text-neutral-400">
                                    Canonical URL
                                </label>

                                <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                    <div className="flex h-[38px] items-stretch overflow-hidden rounded-[10px] bg-white">
                                        <input
                                            type="text"
                                            spellCheck={false}
                                            placeholder="https://www.example.com/"
                                            className="flex-1 border-0 bg-transparent px-2 text-sm leading-6 text-neutral-800 placeholder:text-neutral-400 focus:outline-none"
                                            value={
                                                editor.global.data
                                                    ?.canonical_url || ''
                                            }
                                            onChange={(e) =>
                                                editor.global.change({
                                                    key: 'canonical_url',
                                                    value: e.target.value.trim(),
                                                })
                                            }
                                        />
                                    </div>
                                </div>

                                <p className="mt-2 text-[11px] leading-4 text-neutral-500">
                                    Set the preferred canonical URL for your
                                    site. This helps search engines understand
                                    which version of a page is authoritative.
                                </p>
                            </div>
                            <div className="mt-4 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
                                <div className="mb-1 flex items-center justify-between gap-2">
                                    <label className="block text-[11px] font-medium leading-5 text-neutral-400">
                                        LLMs.txt
                                    </label>
                                    <button
                                        type="button"
                                        onClick={handleGenerateLlmsText}
                                        disabled={llms_generating}
                                        className="inline-flex items-center gap-1 rounded-md bg-primary-700 px-2 py-1 text-[10px] font-semibold text-white shadow-sm transition-colors duration-200 ease-in-out hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {llms_generating ? (
                                            <LoadingSpinner
                                                size="small"
                                                color="#FFFFFF"
                                            />
                                        ) : (
                                            <SparklesIcon className="h-4 w-4" />
                                        )}
                                        <span>Generate</span>
                                    </button>
                                </div>

                                <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                    <div className="flex min-h-[120px] items-stretch overflow-hidden rounded-[10px] bg-white">
                                        <textarea
                                            rows={6}
                                            className="flex-1 resize-y border-0 bg-transparent px-2 py-1.5 text-sm leading-6 text-neutral-800 placeholder:text-neutral-400 focus:outline-none"
                                            value={llmsEditorValue}
                                            onChange={(e) => {
                                                setLlmsEditorValue(
                                                    e.target.value
                                                );
                                                setLlmsUsesGeneratedDefault(
                                                    false
                                                );
                                                editor.global.change({
                                                    key: 'llms_txt',
                                                    value: e.target.value,
                                                });
                                            }}
                                            placeholder={`# llms.txt
# Describe your site for LLMs
# Example:
# Site: ${editor.global.data?.company_name || 'Your Company'}
# Summary:`}
                                        />
                                    </div>
                                </div>

                                <p className="mt-2 text-[11px] leading-4 text-neutral-500">
                                    This content is served at /llms.txt to help
                                    AI systems understand your site.
                                </p>
                                {llmsUsesGeneratedDefault ? (
                                    <p className="mt-1 text-[11px] leading-4 text-neutral-500">
                                        Showing the generated default because no
                                        custom `llms_txt` value is saved yet.
                                        Edit this text to customize it.
                                    </p>
                                ) : null}
                            </div>
                        </section>

                        {/* ===== Custom Code ===== */}
                        <section className="mt-5">
                            <div className="text-md mb-3 flex items-center gap-2 font-bold text-primary-700">
                                <CodeBracketIcon
                                    className="h-5 w-5 text-primary-700"
                                    aria-hidden="true"
                                />
                                <span>Custom Code</span>
                            </div>

                            {tag_error && (
                                <div className="mb-2 rounded-md bg-red-50 px-2 py-1.5 text-xs text-red-700 ring-1 ring-inset ring-red-200">
                                    Failed to get custom code.
                                </div>
                            )}

                            {/* 👇 add vertical spacing between the two accordions */}
                            <div className="flex flex-col gap-3">
                                <CustomCode
                                    variant="head"
                                    custom_code={custom_code}
                                    setCustomCode={setCustomCode}
                                    saving_tags={saving_tags}
                                    getting_tags={getting_tags}
                                    tag_error={tag_error}
                                />
                                <CustomCode
                                    variant="footer"
                                    custom_code={custom_code}
                                    setCustomCode={setCustomCode}
                                    saving_tags={saving_tags}
                                    getting_tags={getting_tags}
                                    tag_error={tag_error}
                                />
                            </div>
                        </section>

                        {/* ===== Redirects ===== */}
                        <section className="mb-2 mt-5">
                            <div className="text-md mb-3 flex items-center gap-2 font-bold text-primary-700">
                                <ArrowsRightLeftIcon
                                    className="h-5 w-5 text-primary-700"
                                    aria-hidden="true"
                                />
                                <span>Redirects</span>
                            </div>

                            <Redirects />
                        </section>

                        {/* ===== Cookie Consent ===== */}
                        <section className="mb-2 mt-5">
                            <div className="text-md mb-3 flex items-center gap-2 font-bold text-primary-700">
                                <ShieldCheckIcon
                                    className="h-5 w-5 text-primary-700"
                                    aria-hidden="true"
                                />
                                <span>Cookie Consent</span>
                            </div>

                            <CookieConsent />
                        </section>

                        <section className="mb-2 mt-5">
                            <div className="text-md mb-3 flex items-center gap-2 font-bold text-primary-700">
                                <ChatBubbleLeftEllipsisIcon
                                    className="h-5 w-5 text-primary-700"
                                    aria-hidden="true"
                                />
                                <span>Nicejob Popup Form</span>
                            </div>

                            <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <label className="mb-1 block text-[11px] font-medium leading-5 text-neutral-400">
                                            Hide footer text
                                        </label>
                                        <p className="text-[11px] leading-4 text-neutral-500">
                                            Keep the NiceJob popup form footer
                                            copy hidden. Turn this off to
                                            restore the default footer text.
                                        </p>
                                    </div>
                                    <div className="flex shrink-0 flex-col items-end gap-1">
                                        <span
                                            className={`text-[10px] font-semibold uppercase tracking-wide ${
                                                hideNicejobPopupFooterText
                                                    ? 'text-primary-700'
                                                    : 'text-neutral-400'
                                            }`}
                                        >
                                            {hideNicejobPopupFooterText
                                                ? 'Enabled'
                                                : 'Disabled'}
                                        </span>
                                        <button
                                            type="button"
                                            role="switch"
                                            aria-checked={
                                                hideNicejobPopupFooterText
                                            }
                                            onClick={() =>
                                                editor.global.change({
                                                    key: 'hide_nicejob_popup_footer_text',
                                                    value: !hideNicejobPopupFooterText,
                                                })
                                            }
                                            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ${
                                                hideNicejobPopupFooterText
                                                    ? 'bg-primary-700'
                                                    : 'bg-neutral-300'
                                            }`}
                                        >
                                            <span
                                                className={`inline-block h-5 w-5 translate-y-0.5 rounded-full bg-white transition-transform duration-200 ${
                                                    hideNicejobPopupFooterText
                                                        ? 'translate-x-[1.3rem]'
                                                        : 'translate-x-0.5'
                                                }`}
                                            />
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-4 border-t border-neutral-200 pt-4">
                                    <label className="mb-1 block text-[11px] font-medium leading-5 text-neutral-400">
                                        Footer text size
                                    </label>
                                    <div className="rounded-xl bg-neutral-300 p-[2px]">
                                        <div className="rounded-[10px] bg-white px-3 py-2">
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="range"
                                                    min={10}
                                                    max={18}
                                                    step={1}
                                                    value={
                                                        nicejobPopupFooterFontSize
                                                    }
                                                    onChange={(e) =>
                                                        editor.global.change({
                                                            key: 'nicejob_popup_footer_font_size',
                                                            value: normalizeNicejobPopupFooterFontSize(
                                                                e.target.value
                                                            ),
                                                        })
                                                    }
                                                    className="tw-slider flex-1 cursor-pointer"
                                                    aria-label="NiceJob popup footer text size"
                                                />
                                                <span className="w-12 text-right text-xs font-semibold text-neutral-700">
                                                    {nicejobPopupFooterFontSize}
                                                    px
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="mt-2 text-[11px] leading-4 text-neutral-500">
                                        Use 10 to 18px. This only changes the
                                        small terms and privacy copy at the
                                        bottom of the NiceJob popup form.
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* ===== Backup ===== */}
                        <section className="mb-2 mt-5">
                            <div className="text-md mb-3 flex items-center gap-2 font-bold text-primary-700">
                                <ArrowUturnLeftIcon
                                    className="h-5 w-5 text-primary-700"
                                    aria-hidden="true"
                                />
                                <span>Backup</span>
                            </div>

                            <Backup />
                        </section>

                        {/* ===== Site Export ===== */}
                        <section className="mb-2 mt-5">
                            <div className="text-md mb-3 flex items-center gap-2 font-bold text-primary-700">
                                <ArrowDownTrayIcon
                                    className="h-5 w-5 text-primary-700"
                                    aria-hidden="true"
                                />
                                <span>Site Export</span>
                            </div>

                            <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
                                <p className="text-[11px] leading-4 text-neutral-500">
                                    Download a functional static site with HTML,
                                    CSS, media, and the JavaScript required for
                                    interactions. Font Awesome files and NiceJob
                                    footer branding are not included. Upload the
                                    extracted files directly to AWS or any
                                    static web host; no build or server command
                                    is required. The HTML pages can also be
                                    opened directly after unzipping.
                                </p>
                                <button
                                    type="button"
                                    onClick={handleSiteExport}
                                    disabled={site_exporting || !company_id}
                                    className="mt-3 inline-flex items-center gap-2 rounded-md bg-primary-700 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {site_exporting ? (
                                        <LoadingSpinner
                                            size="small"
                                            color="#FFFFFF"
                                        />
                                    ) : (
                                        <ArrowDownTrayIcon className="h-4 w-4" />
                                    )}
                                    <span>
                                        {site_exporting
                                            ? 'Preparing export…'
                                            : 'Download site'}
                                    </span>
                                </button>
                            </div>
                        </section>
                    </>
                )}
            </EditorDrawer>
        </>
    );
}

/* ============================== CustomCode (matches Redirects look) ============================== */
function CustomCode(props) {
    const {
        saving_tags,
        getting_tags,
        tag_error,
        custom_code,
        setCustomCode,
        variant,
    } = props;

    const title = variant.charAt(0).toUpperCase() + variant.slice(1);
    const [open, setOpen] = useState(false);

    return (
        <div className="rounded-xl border border-neutral-200 bg-white shadow-sm">
            {/* Header – same as Redirects */}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="group flex w-full items-center justify-between px-4 py-3 text-left"
            >
                <span className="text-[15px] font-medium leading-5 text-neutral-600 group-hover:text-neutral-700">
                    {title}
                </span>
                <i
                    className={`fas fa-chevron-down text-neutral-400 transition-transform group-hover:text-neutral-500 ${
                        open ? 'rotate-180' : ''
                    }`}
                    aria-hidden="true"
                />
            </button>

            {/* Body – divider lives inside so it’s invisible when closed */}
            {open && (
                <div className="px-4 pb-3">
                    <div className="-mx-4 mb-3 h-px w-[calc(100%+2rem)] bg-neutral-200" />

                    <div className="relative">
                        {(saving_tags || getting_tags) && (
                            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                                <LoadingSpinner size="small" />
                            </div>
                        )}

                        <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                            <div className="flex min-h-[120px] items-stretch overflow-hidden rounded-[10px] bg-white">
                                <textarea
                                    rows={6}
                                    className="flex-1 resize-y border-0 bg-transparent px-2 py-1.5 text-sm leading-6 text-neutral-800 placeholder:text-neutral-400 focus:outline-none"
                                    value={custom_code[variant]}
                                    onChange={(e) =>
                                        setCustomCode((prev) => ({
                                            ...prev,
                                            [variant]: e.target.value,
                                        }))
                                    }
                                    disabled={
                                        saving_tags || getting_tags || tag_error
                                    }
                                    placeholder={`Paste HTML exactly as provided, for example:
<script>
console.log('This is an inline script');
</script>
<script src="https://example.com/external-script.js"></script>`}
                                />
                            </div>
                        </div>
                        <p className="mt-2 text-xs leading-5 text-neutral-500">
                            Standard HTML is supported directly. Existing React
                            and Next.js snippets continue to work.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
